import { STEADY, steadyCount } from '../persistence';
import * as THREE from 'three';
import { HabitatVisitors, visitorAtOf, type SpeciesVisitNote, type Visitor, type VisitorAt } from './visitors';
import { ReefDolphinPod, type DolphinDrawn } from './dolphins';
import { ReefOctopuses, type OctopusDrawn } from './octopuses';
import { hash3 } from '../fishSwim';
import { smoothstep as smooth } from '../curve';
import { KEY_AT_FULL_SUN, ROCK_DEG } from '../exhibitView';
import { FishSchools, UNITS_PER_MM, type FishKind, type KindCounts, type PosableFish, type Roam, type Visible } from '../fishSchools';
import { COUNTED_REEF_KINDS, FISH_KINDS } from '../fishKinds';
import { TagMarks, schoolMark, visitorMark, type MarkTarget } from '../tagMarks';
import { BURIED_SHARE, ColonySprites, CORAL_LOOKS, coralOfForm, PALE_SHARE, ROCK_TILES, ReefOutcrops, colonyLook, footprint, laidPart, outcropSeat, partGeometry, seatedPlaces, spanOf, type CoralLook, type OutcropPlace, type Part, type ReefModel, type ReefTextures } from '../reefOutcrops';
import { REEF_BOTTOM } from '../../sim/data/reef/bottom';
import { CORAL_SPECIES, type KitCoral, type Surface } from '../data/coralSpecies';
import { DEPTH_BINS, REEF_ISLANDS, stratumIndex } from '../../sim/reef';
import { REEF_ALGAE } from '../../sim/data/reef/algae';
import { LIMU_SPRITES } from '../../ui/data/limuSprites';
import { LINE_SPRITE, NET_SPRITES } from '../../ui/data/netSprites';
import type { GearPiece } from '../../ui/derelictGear';
import type { Sprite } from '../../ui/data/spriteTypes';
import type { HourSun } from '../sunlight';
import { YOUNG_SEEN_MM, type MauiNuiReef, type MauiNuiReefData } from './data';
import { reefBlend, type ReefBlend } from './reefBlend';
import { HabitatWater } from './water';
import { LightShafts } from './shafts';
import { ReefParticles, pageParticleSettings, type ParticleSettings } from './particles';
import { ReefHunts, huntBudget, type HuntDebug } from './hunts';
import { FEEDERS, LifeAnchors, ReefLife, type Anchor, type LifeDebug, type LifeFish } from './reefLife';
import { PaintedFloor, boulders, cells, drawnFloor, floorSkirt, fogOnly, noise, refinedGrid, skirtLight, type FloorGrid, type FloorShape } from './floor';
import { habitatFov, habitatLights, lightAt, marchComposition, type Composition, type HabitatPick, type HabitatScene } from './habitatScene';
import type { HabitatCameraProfile } from './host';
import { GroundCache, SHADOWS, ShadowBatch, creatureShadows, pageShadowsOn, shadowTexture, slideOf, standingShadow, type Shadow, DRAPE_STEPS } from './shadows';

/**
 * Maui Nui Reef (M118, the user's fifth brief, `docs/MAUI_NUI_REEF.md`: "one continuous reef environment", "one
 * beautiful, living Hawaiian reef, not ... a menu of fragmented reef slices"): the Reef as one habitat, the sanctuary's
 * whole reef from 0 to 30 m drawn as a single reef `lengthM` long across the view, in meters at their true size (its
 * depths eased toward its middle's, `feel`), the surface overhead and the water all round fading into blue. Before it (M110, M117) a scene was one stratum's, an island's
 * reef in a depth band, chosen from twelve. The simulation still holds the reef stratum by stratum; this draws on all of
 * them through `reefBlend`: each point's coral is its strata's live cover by their weights there, its bleaching its
 * bands', its sand and its pavement their bottoms' as NOAA's surveys found them (`REEF_BOTTOM`), its fish the islands'
 * adults a hectare of the whole reef holds on average (`REEF_AREA_M2`), each island's homing where its weight is. Along
 * the reef the shallow band shows at one end, the middle band in its middle and the deep at the other end, each easing
 * into the next (the home view's middle the middle band's alone, the others coming in toward its sides); the islands,
 * every one in every band, drift patch by patch. What it holds is laid on each day (`setData`); how it is laid out is the drawing's
 * own, by the reef's fixed seed (never a stream of the simulation's), so it is the same reef every time it is opened.
 *
 * Its layout, each a rendering choice (JUDGED unless said; the game knows no substrate, `reef.ts`, so where the rock,
 * the pavement and the sand lie is drawn, not simulated, but for how much sand, `REEF_BOTTOM`'s sediment):
 *
 * - `reef`: the reef drawn, `deepM` from its front to its back along the view (seaward, +x) about `x`, and `lengthM`
 *   along it (the brief's "Total explorable reef width: about 60–80 m", "Foreground-to-background habitat depth: about
 *   15–25 m"; M125, 72 m to 96 m, so it runs on past the camera's box either way, 24 m since M127's 48 m box); `floorM` the floor drawn round it, out to where the water hides it (`HABITAT_WATER.fogPerKd`), on a grid of
 *   `stepM`, and its skirt level with its edge out to `skirtM` from the middle (`floorSkirt`, M125); its paint's texel
 *   `texelM`, crisp at a meter, as the user's pixel art is: 0.075 m since M174 (0.1 m before; the user's word: "The
 *   ground pixels are too large ... I do NOT want a huge change. Just make the ground pixels modestly smaller"), the
 *   caustics' web drawn on the same texel and so as much finer ("reduce the pixel size / scale of the caustic pattern by
 *   about the same relative amount"); 0.0375 m since M180 (the user's word: "also make the terrain pixels 50% smaller"),
 *   the caustics' web kept on M174's texel (`causticTexelM`), the ground's alone asked for; its patches, its cobbles, its
 *   stones and its rubble's grains kept their size in meters (`paint.patchTexels`, `reefPaint.cobble.texels`,
 *   `stones.texels`, `rubbleTexels` the more texels), its dither and its edges' fray single texels, as much finer.
 * - The floor: along the reef, the reef's own depth there by its floor (`ReefBlend.depthAt`) taken `feel` of the way
 *   from its middle's (the brief's "Gentle internal terrain variation only", "reef slopes or deepens gently": the floor's
 *   mean 9.5 m over the reef's shallower end and 16.3 m over its deeper, MEASURED by the suite, M177's blocks; M169's
 *   framework 8.9 and 15.8; M125's
 *   9.5 and 16.4; M118's 72 m reef, 9.9 and 16.6); falling seaward by `grade`; kept within the reef's depths by
 *   `marginM`; its hard bottom where a field stands over the level that leaves the strata's share of sand (since M177,
 *   the v5 brief's "Natural sandy channels meandering between reef formations": `framework.hard`, the fore reef's spurs
 *   running seaward, `spur` of it, under patch reefs of two sizes, `patches`, all read through a warp `warpM` across, so
 *   the sand meanders and opens into flats); mounds over it (`mounds`); under each group a knoll (`groups.knoll`,
 *   irregular at its edge, its hard bottom forced only past `knollCore`); and over the hard bottom the reef's framework
 *   (`framework`, M169, the v4 brief's "interconnected reef ridges", "shelves", "ledges", "crevices"): `heightM` high
 *   by its own draw, higher by `gardens` over a knoll and lower by `shallow` where the shallow band holds sway; since
 *   M177 broken into `blocks` (cells `cellM` across, each its own height, the least `low` of the most by its draw to the
 *   `power`, a knoll's at least `knoll` of it; fissures `fissureM` wide between them, cut `fissureDepth` of the way
 *   down), each block's edge as steep as its own draw (`edge`, from a wall to a long slope), its top domed (`dome`) and
 *   partly terraced (`terrace`: `share` of the way to shelves `stepM` apart, their risers the last `riser` of a step),
 *   cut by a `crevice` along its noise's lines, its top `rough`, easing away past `edgeOutM` (the skirt beside the
 *   floor's edge level), never within `underM` of the surface; drawn on a grid of `fine.stepM` where the camera reaches
 *   (`refinedGrid`). MEASURED by the suite (M177): 83 blocks over the reef, their mean heights spread 0.72 of their
 *   mean, 0.71 m; the fissures 0.39 m over the floor, the cores 0.69 m; 0.40 of the hard bottom within 5 cm of a step.
 * - `sand`: of the floor off the knolls, the share drawn sand where the strata's sediment is the reef's mean
 *   (`channels`, the brief's "sandy channels"), more or less as theirs is more or less (NOAA's surveys find it rising
 *   with depth, 5.6% of the shallow band's bottom to 10.3% of the deep's by their floors, MEASURED), within `least` and
 *   `most`.
 * - `groups`: outcrop groups, `main` in the home view (four or five by the reef's seed: the brief's "3–5 prominent coral
 *   outcrop groups visible from the default view"; one in front on the home view's line of sight, two across, one or two
 *   behind) and `side` along the reef either way, found by panning (smaller, `knoll.side`), each moved up to `jitterM`
 *   by its seed (the front one half that), of `each` of the user's Hawaiian kit's outcrops (M110) within `spreadM` of its
 *   middle: since M169 a home view's group one of the kit's `centerpiece` tallest (the v4 brief's "A few large,
 *   distinctive colonies should dominate the scene"), a group along the reef none, its knoll its framework's garden;
 *   where the shallow band holds sway, from the kit's lower half (the brief's "somewhat lower-relief coral").
 * - `ring`: single outcrops past the reef's ends and behind it (M118 to M164; none since M169, `count` and `front` 0, the
 *   framework and its gardens running on past the camera's box instead).
 * - `outcrops`: kept `apart` (of their two widths, middle to middle), none whose top would stand within `underM` of the
 *   surface and none where it would sink past half its height (`BURIED_SHARE`).
 * - `colonies`: the user's coral sprites in gardens on the framework (M169, `reefColonyPlaces`, which says how), each
 *   standing while its draw is under its place's living cover's share of the cover all the colonies drawn make
 *   (`fullPercent`, M125; of the reef's ceiling before), the rest of the cover painted on the pavement; pale, white or
 *   dead as the outcrops' colonies are (`colonyLook`), by its place's bands; each seated at the floor's lowest under
 *   half its width, its picture drawn at its footprint's nearest ground from wherever the camera is (M175,
 *   `FishDraw.ground`; M172's fixed three quarters of its width had reached past its footprint over a ridge or a rock in
 *   front of it), so neither the floor nor the rock it stands over cuts it and what stands in front of it hides it, its
 *   depth at its foot (M172); each where nothing within its footprint rises more than `foot.rise` of its width and
 *   `foot.riseM` over its seat (M175: a ledge's wall or a rock within it had both cut the colony and been drawn over;
 *   MEASURED on the suite's day, the floor within half the colonies' footprints had risen 0.33 m over their seats, a
 *   tenth's 1.0 m), tried elsewhere in its garden first (`foot.tries` of a garden's colonies' count in all, its focal
 *   colony within `foot.focalM` of its middle), then smaller by `foot.shrink` at a time (JUDGED: the limit 0.3 of a
 *   width and 8 cm kept the largest tenth of the colonies 1.39 m across or more, 1.52 m before, where 0.2 and 5 cm had
 *   made it 1.30 m and their full cover 53.7%, under the coral's ceiling, MEASURED).
 * - `boulders`: the drawn basalt before the kit comes (none since M169, `count` 0); `pieceM` the widest of the kit's
 *   rocks and rubble taken as loose pieces.
 * - `rubble`: up to `count` of those pieces (MEASURED: 192 of 320 laid in the tries, M169; M177's blocks, more edges,
 *   all 190 of its 190, its triangles held near M169's), small (`scale`), where the framework meets
 *   the sand (its pavement's share within `edge`), `deeper` larger where the deep band holds sway (the brief's "rocks
 *   become somewhat larger or more dominant"), each seated at the floor's lowest under half its width.
 * - The hard bottom's growths (M177, `cover`, `ReefScene.layCoral`): its turf a film, its coralline in clumps and its
 *   rubble in patches, by the simulation's bottom and the day's coral; its geology (`geology`) limestone but in the deep
 *   band's broad patches of basalt; each point's recess (`recess`: the mean over eight ways of the steepest rise to any
 *   of `radiiM`, `steep` the most), darker in the paint's tones and `light.recess.most` in its light; each standing
 *   colony's foot darkening it (`contact`, to `most` within `full` of its half-width, nothing past `reach`; only a colony within `onFloorM` of the
 *   floor, review M181's: not the outcrops' up on their rock); the kit's
 *   outcrops and loose rocks in the floor's own paint (`rocks`: their turf, coralline, rubble and basalt, their recess
 *   `under` where a face turns down and `foot` within `footM` of the floor).
 * - `bounce`: the light from below, the sand's sent back up off it (M117).
 * - `paint` (M125, the user's sixth brief: "avoid large repetitive square patches", "Use larger coherent material
 *   regions and irregular transitions", its palette's "Golden-tan lobe coral. Dusty pink cauliflower coral. Brown-tan
 *   rice coral. Muted lavender accents"): the floor's paint in patches `patchTexels` across (the whole tank's 6), their
 *   edges warped `patchWarp` of a patch (its 1.2); and the pavement's own coral in the palette's `coral` colors, a
 *   clump each by its draw (golden-tan, dusty pink and brown-tan, and the lavender an accent, one in eight),
 *   `bleached` white: the cover the colonies do not draw (`fullPercent`), laid on the pavement in clumps half a patch
 *   across, 0.2 m (NOAA's colonies at Maui Nui a median 13 to 21 cm long by species, 90% under 29 to 60 cm, MEASURED
 *   by M125's measurer from NCEI 0298217; the kit's drawn at 0.4 to 1.3 m). Its `edges` broken up (M174, the user's
 *   "transitions and edges look too hard, blocky, and artificial ... Do not over-blend everything into mush"): each
 *   patch's outline wandering at half a patch as well (`breakup`, its share of `patchWarp`) and each texel reading its
 *   patch from a point up to half `jitter` texels from it either way, so sand meets rock and pavement, and the coralline's and the
 *   coral's clumps meet the pavement, in a ragged fringe of single pixels, a patch's inside whole; its caustics' contrast varying `amount`
 *   either way over the floor (`causticVary`, by noise `texels` across, 3 m); its recesses' shade cooler as it is darker
 *   (`shadeCool`: its red, green and blue each to that power of the shade, the user's "slightly cool shaded sand in
 *   deeper or shadowed areas"). All JUDGED by eye, the user's to say.
 * - `reefPaint` (M177, the v5 brief's palette, its six materials' hex sRGB before the water's light, the user's word:
 *   "the palette should describe the materials before underwater lighting is applied"; `ReefPaint` says each setting):
 *   M169's and M174's palettes before it, the whole tank's paint's slots, are gone.
 * - `fish`: the reef's fish on cells of `cellM` over the reef from `fromFrontM` behind its front to `toBackM` short of
 *   its back (the home view's middle ground), along it within the camera's box (M127; all along it, M125; M118's thinned past the home view's wedge): every
 *   cell within `nearM` of a group's knoll, where the reef stands up and its fish shelter and feed, and `openKeep` of
 *   the open reef's between, each an island's by the islands' weights there, drawn a block of `blockCells` cells a side at
 *   a time and none left alone of its island (so every school that commutes has its way), so each island's schools home where its character shows; its adults and its
 *   young of `YOUNG_SEEN_MM` or more (M125), each its own schools; each kind's commute drawn `commuteM` at most; each
 *   fish at its own length times `drawnTimes`, every kind alike (M117); every cell's middle `insetM` or more inside the
 *   camera's box (on the cells' odd meters, 3 m), so no school's home is at its edge, and every fish drawn within it
 *   (`FishSchools`' `keepIn`; M127, the user's word: "make sure all the sea creatures locations are within that as
 *   well"; its review's: on the slowest Watch One Day a school's lone palenose drifted 4.88 m past it as it commuted).
 * - `home`: the camera's home view, square to the reef (`yawDeg`), looking at a point `aimM` in from its front,
 *   `aboveFloorM` over the floor, from `backM` away and `upM` above it, `standM` over what is under it at least and
 *   `underM` under the surface at least; its lens `fov`, upright and on its side. Since M177 `upM` 4.6 (3.6 before, the
 *   v5 brief's "some camera positions show a very large empty water column"): its open water 26, 24 and 19% of the
 *   three screens' views, the reef and its coral 66, 67 and 70% (MEASURED by the suite).
 * - `camera`: how near and far it may be from the point it looks at (`distance`; M125, the brief's "Set zoom bounds based
 *   on actual asset sizes and the mobile viewport"): at the nearest the reef's longest fish drawn is seen whole on a
 *   phone upright, the ulua's 1.46 m filling 84% of its width (M151, the user's word "yes" to stopping farther out; at
 *   M125's 1.2 m, set when the kala's 0.75 m was the longest and filled 87%, the ulua's filled 168%, MEASURED on the
 *   suite's day: the phone sees 0.72 m across a meter away); at the farthest a focal formation 4 m across fills a third of it
 *   and the point looked at is seen through 38% of the water's veil, MEASURED on the suite's day; 21% since M169's veil
 *   by distance squared, `light.fogPower`), and the box both keep to: `alongM` along the
 *   reef about its middle, `acrossM` across it from `frontM` in front of the reef, from 0.6 m under the surface to `downM`
 *   (M127, the user's word: "reduce all dimensions by 33%", 48 m by 24 m by 24 m, about M125's middle; M125's, the user's
 *   sixth brief's "an explorable footprint initially around 60–80 m wide and 30–40 m deep", 72 m by 36 m by 36 m; M118's
 *   44 m along it); an `explore` camera (`HabitatCameraProfile.controls`: panned over the floor, the brief's
 *   "Preserve the existing ability to: Move left. Move right. Move forward. Move backward"), turned round `turnDeg` in
 *   all, half of it either way of the home view's direction (the brief's "Initial recommended limit: ±60° from the
 *   default viewing direction"; ±50° since M175, the user's word: "reduce the camera horizontal rotation angle a little
 *   more"); its point kept over the floor's mean within `followM` of it (M169, JUDGED: a camera's
 *   length or so, the framework's ledges under it smoothed away), and never `followUnderM` under the floor there.
 */
export const REEF_SCENE = {
  reef: { x: 6, deepM: 20, lengthM: 96 },
  floorM: 220,
  skirtM: 600,
  stepM: 1.25,
  fine: { stepM: 0.4, x: [-15, 35], z: [-50, 50] },
  texelM: 0.0375,
  causticTexelM: 0.075,
  grade: 0.035,
  feel: 0.25,
  blend: { softQ: 0.1, cellM: 14, swing: 0.8 },
  marginM: 1,
  shallowestM: 1.5,
  spur: { periodM: 15 },
  framework: { heightM: [0.9, 2.2], cellM: 16, knollCore: 0.5, edgeOutM: [85, 105], hard: { spur: 0.45, patches: [[8, 0.8], [3.5, 0.35]], warpM: 3, warpCellM: 9 }, blocks: { cellM: 4.5, low: 0.2, power: 1.4, fissureM: 0.45, fissureDepth: 0.4, knoll: 0.65 }, edge: [0.05, 0.4], dome: { cellM: 2.5, heightM: 0.12 }, terrace: { stepM: 0.4, riser: 0.25, share: 0.7 }, crevice: { cellM: 7, width: 0.03, depth: 0.5 }, rough: [[1.7, 0.15], [0.7, 0.08]], gardens: 0.6, shallow: 0.4, underM: 1.8 },
  geology: { cellM: 12, deepPatch: [0.6, 0.66] },
  reefPaint: {
    limestone: [0xc0b9a5, 0xaca896, 0x989583, 0x838779, 0x68787a, 0x4c6269],
    basalt: [0x829091, 0x64777d, 0x506871, 0x3e5660, 0x304751],
    sand: [0xe9e0ca, 0xded5bc, 0xcbc4ac, 0xb3bdb5, 0x96aaa9],
    turf: [0x97a181, 0x75866a, 0x697a69, 0x566d5b, 0x46594d],
    coralline: [0xb19eab, 0xa08b9d, 0x917d92, 0x786a81, 0x5c5c70],
    rubble: [0xd5cebc, 0xbdb9a9, 0x999e97, 0x7c8583],
    base: 0.75,
    tilt: 0.55,
    recess: 2.6,
    drift: 0.3,
    strata: { bandM: 0.16, width: 0.14 },
    crack: { width: 0.012, share: 0.15 },
    dither: 0.15,
    cobble: { texels: 6, tones: 0.6 },
    lift: 1.25,
    balance: 0.7,
    film: 0.3,
    clump: 0.5,
    sandBase: 0.2,
    sandAtM: 4,
    sandDeep: 2.2,
    ripple: { periodM: 0.45, width: 0.12 },
    stones: { nearReef: 0.4, texels: 4, share: 0.08 },
    rubbleTexels: 4,
    sandCaustics: 1.5,
    relief: { bigTexels: 32, amp: [0.18, 0.06, 0.024], cobbleTilt: 0.15, ripple: 0.012, highlight: 0.06, cavity: 0.07, stoneLift: 1.247 },
    corallineFilm: 0.55,
  },
  cover: { turf: 0.14, macroalgae: 1, coralline: 3, rubble: [0.005, 0.3] },
  recess: { radiiM: [0.4, 0.9, 1.8], steep: 1.2 },
  contact: { reach: 1.15, full: 0.6, most: 0.55, onFloorM: 0.3 },
  rocks: { outcrops: [0.1, 0.03, 0, 1], pieces: [0.05, 0.1, 0.5, 0.15], under: 1, foot: 0.6, footM: 0.3 },
  mounds: [{ cellM: 13, heightM: 0.9 }, { cellM: 4, heightM: 0.25 }],
  sand: { channels: 0.5, least: 0.15, most: 0.75 },
  groups: {
    main: [[1.2, 0.4], [5.5, -3.4], [6.5, 3.8], [11, -0.8], [13, 6.4]],
    side: [[2, -12.5], [9, -17], [3, -23.5], [11, -28.5], [5, -33.5], [9.5, -39], [3, -44], [2, 12.5], [9, 17.5], [3.5, 24], [11, 29], [5, 34], [2.5, 39.5], [10, 44.5]],
    jitterM: 1.5,
    each: { main: [1, 1], side: [0, 0] },
    spreadM: { main: 3.4, side: 2.6 },
    apart: 0.24,
    centerpiece: 3,
    compactM: 4.7,
    tallest: 5,
    knoll: { heightM: [0.5, 0.9], radiusM: [3.5, 4.5], side: 0.75 },
  },
  ring: { count: 0, front: 0 },
  outcrops: { apart: 0.3, underM: 0.8 },
  colonies: { gardens: 380, each: [4, 12], together: 0.8, radiusM: [1.2, 3], high: 2, x: [-9, 30], z: [-44, 44], own: 0.65, focalFrom: 0.85, size: { atMedian: 0.55, power: 1, least: 0.3, most: 1.6 }, gather: 1, pavementM: 0.35, basaltReach: 1, clearOf: 0.4, foot: { rise: 0.3, riseM: 0.08, shrink: 0.8, tries: 12, focalM: 1 } },
  boulders: { count: 0, sizeM: [0.35, 1.5], pieceM: 3, scale: [0.5, 1], nearGroups: 0, nearM: 6, deeper: 0.7 },
  rubble: { count: 190, scale: [0.15, 0.4], edge: [0.15, 0.7], deeper: 0.5 },
  halimeda: { canopyM: [[10, 0.127], [20, 0.181], [30, 0.199], [40, 0.251]], spots: 40000, onRockM: 0.02 },
  limuPatches: { cellM: 6, power: 6, least: 0 },
  padina: { heightM: 0.04 },
  asparagopsis: { heightM: 0.15 },
  microdictyon: { heightM: 0.035 },
  dictyota: { heightM: 0.1 },
  crust: { widthM: 0.113, drawnShare: 0.1 },
  caulerpa: { heightM: 0.08 },
  limuShown: { leastM: 0.25 },
  // The derelict gear snagged on the reef (M338): each kind's width as drawn, m (JUDGED to be seen from the camera's
  // home, as the limu were drawn larger at the user's word, M245: at 0.6 m and less, M338's first draft, the line's pale
  // tangles were lost on the pavement in the browser and no piece could be picked out); on the floor's hard bottom alone
  // (`hardFrom` of its pavement at the spot at least: nets and line snag on the reef's rock, not on its sand), within
  // `alongM` of the reef's middle along it (GAME CHOICE: where the camera first looks, so a cleanup's work is seen
  // without searching the 96 m), each at the first of `tries` draws seated at its full width.
  gear: { largeM: 3.2, smallM: 1.6, lineM: 1.1, hardFrom: 0.5, alongM: 20, tries: 400 },
  bounce: 0x6f6a58,
  light: { fogPower: 2, fill: 0xeef0ea, recess: { most: 0.3 }, keyGain: 2, fillDrop: 0.75 },
  paint: { patchTexels: 10.6, patchWarp: 1.6, edges: { breakup: 0.5, jitter: 1.6 }, causticVary: { amount: 0.35, texels: 40 }, shadeCool: [1.3, 1.1, 0.8], coral: [0xc9a35a, 0xc98f8f, 0x9a7650, 0xc9a35a, 0xb3866a, 0x9a7650, 0xc98f8f, 0x9c8aa8], bleached: 0xf0ede4 },
  fish: { youngCruiseBL: 0.8, cellM: 2, fromFrontM: 4, toBackM: 5, nearM: 1.5, openKeep: 0.1, blockCells: 3, commuteM: 4, drawnTimes: 1.75, insetM: 2 },
  home: { aimM: 7, aboveFloorM: 1, backM: 10, yawDeg: 0, upM: 4.6, standM: 2.2, underM: 0.9 },
  fov: { landscape: 40, portrait: 52 },
  camera: { distance: [2.4, 16], alongM: 48, acrossM: 24, frontM: 8, downM: 24, turnDeg: 100, followM: 2.5, followUnderM: 0.8 },
} as const;

/**
 * The reef's current the water's particles drift on, m/s, scene axes: along the reef (z), at 0.05 m/s (M173). Maui Nui's
 * channels' long-term flow is "weak (0.05–0.10 m s−1)", and measured 25 m down in the Pailolo Channel a mean of 0.04 m/s
 * (Storlazzi et al. 2017, *Front. Mar. Sci.* 4: 381, p. 2, CC BY, compiling Sun 1996 and Flament and Lumpkin 1996): the
 * channels', not the reef floor's (USGS's measurements on Maui Nui's fore reefs unread, their host refused here); its
 * way along the reef JUDGED, steady, the simulation holding no current at the reef.
 */
const REEF_CURRENT: readonly [number, number, number] = [0, 0, 0.05];

/**
 * How the Reef's schools roam it by day (M159, the user's word: "their routes need to be bigger and traverse much more of
 * the reef"; `FishSchools`' `Roam`): each school's middle round a loop from its shelter through `points` more of the
 * reef's cells in view (JUDGED), at `blPerS` of its drawn lengths a second (JUDGED: short of its foraging swim,
 * `FORAGE_BL_PER_S`'s 0.6, so its fish keep up and mill about it), its fish leaning wholly its way (`lean`, as a school
 * on its commute does: M159's review found them leaning 0.6 left each school drifting ahead of its middle, 132 lengths
 * at most, a tenth of the Reef's fish-frames pressed against the box they are kept in); anywhere on the reef in the
 * view, the home ranges measured for all but the roi larger than the view (the redlip's 8,712 m², the bullethead's and the palenose's commutes of hundreds of
 * meters, the jacks' of kilometers, `fishKinds.ts`), the roi within its own (1,236 m², its loop within 19.8 m of its
 * shelter, a disc of that area); its middle `insetM` inside the box (JUDGED: a school's spread). Its commute's own swim
 * (`commuteM`, drawn 4 m at most) is kept beside it. Each fish keeps over the highest floor within `envelopeM` (JUDGED:
 * about two lengths of the reef's middling fish as drawn), read on a grid of `envelopeCell`, so it rises over an outcrop
 * as it comes to it, not at its edge.
 */
const REEF_ROAM: Roam = { points: 6, blPerS: 0.5, lean: 1, rangeM: (k) => (k.swim.homeRangeM2 ? Math.sqrt(k.swim.homeRangeM2 / Math.PI) : null), insetM: 1.5, envelopeM: 1, envelopeCell: 0.25 };

/**
 * Fewer fish than this in the hectare are said to be few (`ReefScene.scarcity`, M117; its adults and, since M125, its
 * young of `YOUNG_SEEN_MM` or more): the brief's least for a default view, "approximately 15–30", less a third (JUDGED).
 */
export const SCARCE = 10;

/** How far apart the interactions' hard bottom and sand are sampled, m (M265; JUDGED: a parrotfish's few bites a place apart). */
const LIFE_GRID_M = 0.75;

/** The floor's grid: `stepM` apart, `fine.stepM` within `fine`'s box, where the camera reaches (M169). */
function reefGrid(): FloorGrid {
  const s = REEF_SCENE;
  return refinedGrid(s.floorM, s.stepM, s.fine.stepM, s.fine.x, s.fine.z);
}

/** The reef's blend as this scene lays it (`reefBlend`), along its length. */
export function sceneBlend(reef: MauiNuiReef): ReefBlend {
  const b = REEF_SCENE.blend;
  return reefBlend(reef, REEF_SCENE.reef.lengthM, b.softQ, b.cellM, b.swing);
}

/** The floor's depth along the reef, m, before its spurs, mounds and knolls: the reef's own there, `feel` of the way from its middle's, falling seaward. */
function depthBefore(blend: ReefBlend, x: number, z: number, middle = blend.depthAt(0)): number {
  const s = REEF_SCENE;
  return middle + s.feel * (blend.depthAt(z) - middle) + s.grade * (x - s.reef.x);
}

/** A group of outcrops: where it stands, the knoll of basalt under it, how many outcrops it has and how far they spread. */
export interface ReefGroup {
  x: number;
  z: number;
  main: boolean;
  knollM: number;
  reachM: number;
  each: readonly [number, number];
  spreadM: number;
}

/** The reef's outcrop groups, by its seed: the home view's, then the rest along the reef. */
export function reefGroups(reef: MauiNuiReef, blend: ReefBlend = sceneBlend(reef)): ReefGroup[] {
  const g = REEF_SCENE.groups;
  const seed = reef.seed;
  // Four or five in the home view: the fifth's slot, behind and to the side, by the reef's own draw.
  const main = g.main.slice(0, hash3(seed, 0, 140) < 0.5 ? 4 : 5);
  return [...main.map((slot) => [slot, true] as const), ...g.side.map((slot) => [slot, false] as const)].map(([[sx, sz], isMain], k) => {
    // The front group moved least, so the reef opens on it (`home`).
    const jitter = g.jitterM * (k === 0 ? 0.5 : 1);
    const [x, z] = [sx + (hash3(seed, k, 141) - 0.5) * 2 * jitter, sz + (hash3(seed, k, 142) - 0.5) * 2 * jitter];
    // No knoll higher than leaves the tallest outcrop (1.4 m, MEASURED, M110) `underM` under the surface and half a meter
    // more (JUDGED, room for the knoll's noisy edge); 40% lower where the shallow band alone holds sway (JUDGED, its lower
    // relief).
    const most = Math.max(0, depthBefore(blend, x, z) - 1.4 * 1.25 - REEF_SCENE.outcrops.underM - 0.5);
    const size = (isMain ? 1 : g.knoll.side) * (1 - 0.4 * (blend.bandsAt(z)[0] as number));
    return {
      x, z, main: isMain,
      knollM: Math.min(most, size * (g.knoll.heightM[0] + (g.knoll.heightM[1] - g.knoll.heightM[0]) * hash3(seed, k, 143))),
      reachM: size * (g.knoll.radiusM[0] + (g.knoll.radiusM[1] - g.knoll.radiusM[0]) * hash3(seed, k, 144)),
      each: isMain ? g.each.main : g.each.side,
      spreadM: isMain ? g.spreadM.main : g.spreadM.side,
    };
  });
}

/** A floor shape the reef's camera and fish read: its height and its pavement's share at a point. */
export type ReefFloor = FloorShape;

/** Each stratum's bottom (`REEF_BOTTOM`), in the reef's strata's order. */
function bottomsOf(reef: MauiNuiReef): (typeof REEF_BOTTOM)[number][] {
  return reef.strata.map((l) => REEF_BOTTOM[stratumIndex(l.islandIndex, l.binIndex)] as (typeof REEF_BOTTOM)[number]);
}

/**
 * A *Halimeda*'s height at a depth, m (M243): the canopy Spalding measured in the *H. kanaloana* meadows off west Maui,
 * 12.7 cm at 10 m, 18.1 at 20, 19.9 at 30 and 25.1 at 40 (Spalding 2012, PhD dissertation, University of Hawaiʻi, as
 * M226's reader read it; ScholarSpace answered M243's with a bot check), straight between, its shallowest above 10 m
 * where none was measured (JUDGED); a sand meadow's canopy standing in for NOAA's *Halimeda* of any species (JUDGED).
 */
export function halimedaHeightM(depthM: number): number {
  const c = REEF_SCENE.halimeda.canopyM;
  if (depthM <= (c[0] as readonly number[])[0]!) return (c[0] as readonly number[])[1]!;
  for (let i = 1; i < c.length; i++) {
    const [[d0, h0], [d1, h1]] = [c[i - 1] as readonly [number, number], c[i] as readonly [number, number]];
    if (depthM <= d1) return h0 + ((h1 - h0) * (depthM - d0)) / (d1 - d0);
  }
  return (c[c.length - 1] as readonly number[])[1]!;
}

/** Each of a reef's strata's limu of a NOAA code, percent of its bottom as NOAA's surveys named it (`REEF_ALGAE`, M224); none where unsurveyed. */
export function limuOf(reef: MauiNuiReef, code: 'HALI' | 'PADI' | 'ASPP' | 'MICR' | 'DICO' | 'PESP' | 'LOBO' | 'CAUL'): number[] {
  return reef.strata.map((l) => REEF_ALGAE.find((a) => a.island === REEF_ISLANDS[l.islandIndex] && a.bin === DEPTH_BINS[l.binIndex]?.bin)?.percent[code] ?? 0);
}

/** Each of a reef's strata's *Halimeda* (`HALI`, M243). */
export const halimedaOf = (reef: MauiNuiReef): number[] => limuOf(reef, 'HALI');

/** A crust's height drawn: its patch's width across, in its picture's proportion (M244). */
const crustHeightM = (sprite: Sprite): number => (REEF_SCENE.crust.widthM * sprite.height) / sprite.width;

/**
 * The limu the Reef draws (M243, M244): each its sprites, NOAA's code for it, its spots' two draws (whether one stands,
 * which variant) and its height at a depth (its sprite's for a crust). The Bishop Museum's Hawaiian algae key
 * (hbs.bishopmuseum.org/botany/algaekey, read for M244; its terms not stated, its facts cited) and the rest:
 * - *Halimeda*: Spalding's canopy by depth (`halimedaHeightM`, M243).
 * - *Padina*: 4 cm, the middle of *P. melemele*'s "Plants 1-7 cm tall" (Abbott 1996, *Pacific Science* 50(2), p. 143;
 *   at 10 to 36 m off Kauaʻi and Molokaʻi, p. 144; JUDGED, the key's *Padina* page giving no size).
 * - *Asparagopsis*: 15 cm, the middle of the key's "Gametophytes 10-20 cm tall" (JUDGED).
 * - *Microdictyon*: 3.5 cm, the middle of the key's "stiff clumps to 1 cm tall if in turf with other algae, or large,
 *   emergent clumps to 6 cm tall when isolated" (JUDGED).
 * - NOAA's *Dictyopteris* and *Dictyota*, one code: 10 cm, half the key's *Dictyota acutiloba* "to 20 cm long" (JUDGED: a top, no
 *   middle given), *Dictyota* the more often met in NOAA's photoquadrats at Maui and Molokaʻi (PIFSC Cruise Report CR-08-013, 2008, its cruise of 2006,
 *   Tables G.3.1.1 and H.3.1.1) than *Dictyopteris*, "15-32 cm tall" (the key).
 * - *Peyssonnelia*, a crust: `crust.widthM` across, a 100 cm² patch's width, "Larger unconsolidated patches (100 cm2)"
 *   of the related *Ramicrusta hawaiiensis* off west Hawaiʻi (Grady et al. 2022, *Frontiers in Marine Science* 9:
 *   1009471, CC BY, p. 12), none of Hawaiʻi's *Peyssonnelia* measured in what could be read (JUDGED).
 * - *Lobophora*: the same crust's width (JUDGED, no size of it read; NOAA's annotation manual sets the two side by side,
 *   "uncalcified like the brown EMA Lobophora spp. or calcified like red Peyssonnelia spp.", Lamirand et al. 2022,
 *   PIFSC H-22-03, p. 110).
 * - *Caulerpa*: 8 cm, the middle of *C. racemosa*'s "Upright branches 1-15 cm high" (UH Botany's ReefWatcher's field
 *   guide, its terms not stated, its facts cited; JUDGED), the grape-like *Caulerpa* the user's picture draws.
 *
 * `hard`, whether it grows on the hard bottom alone (M245, the user's report: the crusts strewn over the sand "make it
 * look diseased like chicken pox"): *Peyssonnelia*'s and *Lobophora*'s crusts on rock ("forms tightly adherent crusts
 * ... on reef structures", Grady et al. 2022, p. 7; NOAA's annotation manual's encrusting macroalgae "completely attached
 * to the substrate"), *Padina* on "intertidal benches" (the key), *Asparagopsis* on reef edges "in areas of constant
 * water motion" (the key), *Dictyota* that "Grows on rock" (the key's *D. acutiloba*); the rest on sand as well:
 * *Halimeda* "on rock, coral, or in sand" (the key), *Microdictyon* "on limestone benches, basalt rocks, coral reefs and
 * rubble, often in sandy areas" (the key), *Caulerpa*'s runners that "anchor mats to rocks and sand" (UH Botany).
 */
const REEF_LIMU = [
  { plant: 'halimeda', code: 'HALI', hard: false, salts: [245, 246], heightM: (depthM: number): number => halimedaHeightM(depthM) },
  { plant: 'padina', code: 'PADI', hard: true, salts: [247, 248], heightM: (): number => REEF_SCENE.padina.heightM },
  { plant: 'asparagopsis', code: 'ASPP', hard: true, salts: [249, 250], heightM: (): number => REEF_SCENE.asparagopsis.heightM },
  { plant: 'microdictyon', code: 'MICR', hard: false, salts: [251, 252], heightM: (): number => REEF_SCENE.microdictyon.heightM },
  { plant: 'dictyota', code: 'DICO', hard: true, salts: [253, 254], heightM: (): number => REEF_SCENE.dictyota.heightM },
  { plant: 'peyssonnelia', code: 'PESP', hard: true, salts: [255, 256], heightM: (_depthM: number, sprite: Sprite): number => crustHeightM(sprite) },
  { plant: 'lobophora', code: 'LOBO', hard: true, salts: [257, 258], heightM: (_depthM: number, sprite: Sprite): number => crustHeightM(sprite) },
  { plant: 'caulerpa', code: 'CAUL', hard: false, salts: [259, 260], heightM: (): number => REEF_SCENE.caulerpa.heightM },
] as const;
type ReefLimu = (typeof REEF_LIMU)[number]['plant'];

/** The reef's floor (`REEF_SCENE`'s layout), the same every time, and the share of sand it asks for at a point. */
export function reefFloor(reef: MauiNuiReef, blend: ReefBlend = sceneBlend(reef)): ReefFloor & { sandAt(x: number, z: number): number; reliefAt(x: number, z: number): number; basaltAt(x: number, z: number): number } {
  const s = REEF_SCENE;
  const seed = reef.seed;
  const deepest = reef.depthRangeM[1] + s.marginM;
  const middle = blend.depthAt(0);
  const groups = reefGroups(reef, blend);
  const bottoms = bottomsOf(reef);
  // The reef's mean sediment, each stratum by its floor; a point's its strata's by their weights there.
  const meanSediment = bottoms.reduce((m, b, j) => m + b.sedimentPercent * (reef.areaShare[j] as number), 0);
  const sedimentAt = (x: number, z: number): number => blend.strataAt(x, z).reduce((m, w, j) => m + w * (bottoms[j] as (typeof bottoms)[number]).sedimentPercent, 0);
  const f = s.framework;
  const h = f.hard;
  // Where the reef's hard bottom stands (M177, the v5 brief's "Natural sandy channels meandering between reef
  // formations", "Rounded ... outcrops"): the fore reef's spurs, faint, under patch reefs of two sizes, all read through a
  // warp, so the sand between meanders and widens into flats rather than running in straight grooves.
  const spurOf = (x: number, z: number): number => {
    const [wx, wz] = [x + h.warpM * (noise(seed, 31, x, z, h.warpCellM) - 0.5) * 2, z + h.warpM * (noise(seed, 32, x, z, h.warpCellM) - 0.5) * 2];
    const spur = Math.cos((2 * Math.PI * wz) / s.spur.periodM + 2.2 * (noise(seed, 1, x, z, 20) - 0.5) * Math.PI) + 0.9 * (noise(seed, 5, x, z, 9) - 0.5);
    return h.spur * spur + h.patches.reduce((m, [cellM, weight], k) => m + (weight as number) * (noise(seed, 33 + k, wx, wz, cellM as number) - 0.5) * 2, 0);
  };
  // The field's own spread over the reef, sampled once, so a share of sand is the level under which that share of it lies
  // (over a third of the reef, a few of its grooves, the sand drawn wanders from the share asked by where they fall:
  // MEASURED, off the knolls, 0.36, 0.23 and 0.62 drawn along it for 0.30, 0.42 and 0.55 asked; a level read a groove's
  // cycle either way of each point moved the middle to 0.29 and the shallower end to 0.39, no nearer on the whole).
  const half = s.reef.lengthM / 2;
  const front = s.reef.x - s.reef.deepM / 2;
  const sample: number[] = [];
  for (let x = front; x <= front + s.reef.deepM; x += 0.5) for (let z = -half; z <= half; z += 0.5) sample.push(spurOf(x, z));
  sample.sort((a, b) => a - b);
  const under = (share: number): number => sample[Math.min(sample.length - 1, Math.max(0, Math.floor(share * sample.length)))] as number;
  const sandAt = (x: number, z: number): number => Math.min(s.sand.most, Math.max(s.sand.least, (s.sand.channels * sedimentAt(x, z)) / Math.max(1e-9, meanSediment)));
  /**
   * How far into a group's knoll a point is, 0 outside to 1 on its top, its edge broken by noise (irregular basalt): flat
   * over its middle two thirds, where its outcrops stand, falling smoothly to the floor round it (M117). Past its
   * reach's most (its noise under 1) nothing, neither its noise nor its distance drawn: the fish ask the floor's height often.
   */
  const knollAt = (x: number, z: number, k: number): number => {
    const g = groups[k] as ReefGroup;
    const [dx, dz] = [x - g.x, z - g.z];
    if (dx * dx + dz * dz >= (1.2 * g.reachM) ** 2) return 0;
    const r = Math.hypot(dx, dz) / (g.reachM * (0.8 + 0.4 * noise(seed, 9 + k, x, z, 3)));
    const t = Math.min(1, Math.max(0, (1 - r) / 0.35));
    return t * t * (3 - 2 * t);
  };
  /**
   * How far into the reef's framework a point is (M169, the v4 brief's "interconnected reef ridges"): above 0 on the
   * spurs, where they rise over the level that leaves the strata's share of sand in the grooves between (the brief's
   * "sandy channels"), more the further in; and over a group's knoll at least its share (the brief's "elevated coral
   * gardens", where its outcrops stand); and the most a group's knoll there.
   */
  const reefAt = (x: number, z: number): [number, number] => {
    let knoll = 0;
    for (let k = 0; k < groups.length; k++) knoll = Math.max(knoll, knollAt(x, z, k));
    return [Math.max(spurOf(x, z) - under(sandAt(x, z)), 1.2 * knoll - f.knollCore), knoll];
  };
  /** The floor's depth under the framework: the reef's own, eased and falling seaward, and its mounds. */
  const baseAt = (x: number, z: number): number => {
    let depth = depthBefore(blend, x, z, middle);
    for (const [n, m] of s.mounds.entries()) depth -= m.heightM * (noise(seed, 2 + n, x, z, m.cellM) - 0.5) * 2;
    return depth;
  };
  const heightAt = (x: number, z: number): number => {
      let depth = baseAt(x, z);
      // The framework over the floor (M169): its height by its own draw over `cellM`, higher where a group stands and
      // lower where the shallow band holds sway; never within `underM` of the surface, so a colony's sprite on it stays
      // under the water. Since M177 (the v5 brief's "Irregular rock terraces and shelves", "Rounded volcanic and limestone
      // outcrops", "Small changes in elevation across continuous hardbottom", "Natural fissures and fractures"): broken
      // into `blocks`, each its own height (the lowest `low` of the tallest, so much of the hard bottom stays low), its
      // edge as steep as its own draw (`edge`, from a wall to a long slope), fissures between blocks cut part way down
      // (`fissureM` wide, `fissureDepth` deep), its top domed (`dome`) and partly terraced (`terrace`: shelves `stepM`
      // apart, their risers the last `riser` of a step), a crevice cut along the noise's lines, its top rough.
      const [o, knoll] = reefAt(x, z);
      if (o > -f.edge[0]) {
        const b = f.blocks;
        const tall = (f.heightM[0] + (f.heightM[1] - f.heightM[0]) * noise(seed, 21, x, z, f.cellM)) * (1 + f.gardens * knoll) * (1 - f.shallow * (blend.bandsAt(z)[0] as number));
        const [near, next, id] = cells(seed, 35, x, z, b.cellM);
        const block = b.low + (1 - b.low) * id ** b.power;
        const edge = f.edge[0] + (f.edge[1] - f.edge[0]) * hash3(seed, Math.floor(id * 1e6), 36);
        const fissure = smooth(0, b.fissureM, next - near);
        let rise = tall * Math.max(block, b.knoll * knoll) * smooth(0, edge, o) * (1 - b.fissureDepth * (1 - fissure));
        rise += f.dome.heightM * tall * smooth(0, edge, o) * (noise(seed, 37, x, z, f.dome.cellM) - 0.5) * 2;
        const step = f.terrace.stepM;
        const t = Math.max(0, rise) / step;
        rise += f.terrace.share * (step * (Math.floor(t) + smooth(1 - f.terrace.riser, 1, t - Math.floor(t))) - Math.max(0, rise));
        const inside = smooth(0.15, 0.3, o);
        rise -= f.crevice.depth * tall * inside * (1 - smooth(0, f.crevice.width, Math.abs(noise(seed, 23, x, z, f.crevice.cellM) - 0.5)));
        for (const [k, [cellM, heightM]] of f.rough.entries()) rise += smooth(0, f.edge[0], o) * (heightM as number) * (noise(seed, 24 + k, x, z, cellM as number) - 0.5) * 2;
        // Easing away toward the floor's edge, far past where the water hides it, so the skirt beside it lies smooth (M177).
        rise *= 1 - smooth(f.edgeOutM[0], f.edgeOutM[1], Math.max(Math.abs(x), Math.abs(z)));
        depth -= Math.max(0, Math.min(rise, depth - s.shallowestM - f.underM));
      }
      return -Math.min(deepest, Math.max(s.shallowestM, depth));
  };
  return {
    heightAt,
    /** How high the framework stands over the floor under it at a point, m (M169: where its gardens grow). */
    reliefAt: (x, z) => heightAt(x, z) + Math.min(deepest, Math.max(s.shallowestM, baseAt(x, z))),
    pavementAt(x, z) {
      // The framework reef, the grooves between sand.
      return smooth(-0.08, 0.08, reefAt(x, z)[0]);
    },
    sandAt,
    /**
     * How much of the hard bottom at a point is basalt, 0 limestone to 1 (M177, the v5 brief's "Introduce darker
     * volcanic rock where geologically appropriate", "Do not cover the entire reef with dark volcanic rock"; JUDGED, the
     * game knows no substrate map): where the deep band holds sway, broad patches of it (`geology`), the framework's
     * carbonate thinner there; the rest limestone, the kit's outcrops the basalt standing through it (`rocks`).
     */
    basaltAt(x, z) {
      const g = s.geology;
      return (blend.bandsAt(z)[2] as number) * smooth(g.deepPatch[0], g.deepPatch[1], noise(seed, 61, x, z, g.cellM));
    },
  };
}

/**
 * Where the outcrops stand: each group's outcrops about its knoll's middle (a centerpiece, then the rest), where the
 * shallow band holds sway from the kit's lower half; then single outcrops past the reef's ends and behind it, and in
 * front of the camera's box (`ring.front`, last, so the loose colonies' anchors are the rest in their order). Each kept
 * off the others, under the surface and not sunk past half its height.
 */
export function reefOutcropPlaces(reef: MauiNuiReef, model: ReefModel, floor: ReefFloor, tops: readonly number[], blend: ReefBlend = sceneBlend(reef)): OutcropPlace[] {
  const s = REEF_SCENE;
  const seed = reef.seed;
  const widths = model.outcrops.map((o) => o.widthM);
  const byHeight = model.outcrops.map((_, v) => v).sort((a, b) => (tops[b] ?? 0) - (tops[a] ?? 0) || a - b);
  const compactOf = (pool: readonly number[]): number[] => pool.filter((v) => (widths[v] ?? 0) <= s.groups.compactM).slice(0, s.groups.tallest);
  const tall = { centerpieces: byHeight.slice(0, s.groups.centerpiece), rest: compactOf(byHeight) };
  const lowHalf = byHeight.slice(Math.floor(byHeight.length / 2));
  const low = { centerpieces: lowHalf.slice(0, s.groups.centerpiece), rest: compactOf(lowHalf) };
  const places: OutcropPlace[] = [];
  /** An outcrop tried at a point: placed unless it would stand too near the surface or another, or sink past half. */
  const tryAt = (x: number, z: number, variant: number, column: number, yaw: number, apart: number): boolean => {
    const y = floor.heightAt(x, z);
    const w = widths[variant] ?? 4;
    if (y + (tops[variant] ?? 0) > -s.outcrops.underM) return false;
    if (places.some((p) => Math.hypot(p.at.x - x, p.at.z - z) < apart * (w + (widths[p.variant] ?? 4)))) return false;
    const place: OutcropPlace = { column, j: places.length, at: new THREE.Vector3(x, y, z), up: new THREE.Vector3(0, 1, 0), variant, yaw };
    const seat = outcropSeat(model, place, variant, 1, (fx, fz) => floor.heightAt(fx, fz));
    if (seat.gap > BURIED_SHARE * seat.height) return false;
    places.push(place);
    return true;
  };
  const groups = reefGroups(reef, blend);
  for (const [c, g] of groups.entries()) {
    const pools = (blend.bandsAt(g.z)[0] as number) > 0.5 ? low : tall;
    const n = g.each[0] + Math.floor(hash3(seed, c, 113) * (g.each[1] - g.each[0] + 1));
    for (let t = 0, made = 0; made < n && t < n * 16; t++) {
      const pool = made === 0 ? pools.centerpieces : pools.rest;
      const variant = pool[Math.floor(hash3(seed * 31 + c, t, 114) * pool.length)] as number;
      const a = 2 * Math.PI * hash3(seed * 31 + c, t, 115);
      const d = made === 0 && t < 4 ? 0 : g.spreadM * Math.sqrt(hash3(seed * 31 + c, t, 116));
      if (tryAt(g.x + d * Math.cos(a), g.z + d * Math.sin(a), variant, c * 64 + made, 2 * Math.PI * hash3(seed * 31 + c, t, 117), s.groups.apart)) made++;
    }
  }
  // The ring: any of the kit's outcrops, alone, past the reef's ends and behind it, out into the fog (JUDGED: to 24 m
  // behind it and 26 m past its ends, where the water hides 63% of what lies 33 to 40 m off).
  const half = s.reef.lengthM / 2;
  const [front, back] = [s.reef.x - s.reef.deepM / 2, s.reef.x + s.reef.deepM / 2];
  for (let t = 0, made = 0; made < s.ring.count && t < s.ring.count * 8; t++) {
    const x = front + (back + 24 - front) * hash3(seed, t, 150);
    const z = (half + 26) * (2 * hash3(seed, t, 151) - 1);
    if (x < back + 1 && Math.abs(z) < half + 1) continue;
    const variant = Math.floor(hash3(seed, t, 152) * model.outcrops.length);
    if (tryAt(x, z, variant, (groups.length + 1) * 64 + made, 2 * Math.PI * hash3(seed, t, 153), s.outcrops.apart)) made++;
  }
  // And in front of the camera's box, 3 m clear of it, the way the camera looks when turned to the shore.
  const shore = front - s.camera.frontM - 3;
  for (let t = 0, made = 0; made < s.ring.front && t < s.ring.front * 8; t++) {
    const x = shore - 24 * hash3(seed, t, 154);
    const z = (half + 26) * (2 * hash3(seed, t, 155) - 1);
    const variant = Math.floor(hash3(seed, t, 156) * model.outcrops.length);
    if (tryAt(x, z, variant, (groups.length + 2) * 64 + made, 2 * Math.PI * hash3(seed, t, 157), s.outcrops.apart)) made++;
  }
  return places;
}

/** A coral's colonies' length at a draw, 0 to 1, cm: read between its twentieths (`CORAL_SPECIES.lengthsCm`). */
export function colonyLengthCm(coral: KitCoral, u: number): number {
  const q = CORAL_SPECIES.lengthsCm[coral];
  const at = Math.min(q.length - 1 - 1e-9, Math.max(0, u * (q.length - 1)));
  const k = Math.floor(at);
  return (q[k] as number) + ((q[k + 1] as number) - (q[k] as number)) * (at - k);
}

/**
 * How much of each of the user's four corals a place grows (M169), relative: its strata's colonies of each by their
 * weights there (NOAA's surveys, `CORAL_SPECIES.strata`, each stratum's as shares of its four), times how much more or
 * less common each is on the place's surface than on all three (`CORAL_SPECIES.surfaces`).
 */
export function coralWeights(strata: readonly number[], surface: Surface): Record<KitCoral, number> {
  const kit = Object.keys(CORAL_SPECIES.surfaces.framework) as KitCoral[];
  const total = (r: Readonly<Record<KitCoral, number>>): number => kit.reduce((m, k) => m + r[k], 0);
  const all = kit.map((k) => (Object.keys(CORAL_SPECIES.surfaces) as Surface[]).reduce((m, s) => m + CORAL_SPECIES.surfaces[s][k], 0));
  const allTotal = all.reduce((a, b) => a + b, 0);
  const on = CORAL_SPECIES.surfaces[surface];
  return Object.fromEntries(kit.map((k, i) => {
    const local = strata.reduce((m, w, j) => {
      const r = CORAL_SPECIES.strata[j] as Readonly<Record<KitCoral, number>>;
      return m + w * (r[k] / Math.max(1, total(r)));
    }, 0);
    return [k, local * (on[k] / total(on)) / ((all[i] as number) / allTotal)];
  })) as Record<KitCoral, number>;
}

/**
 * Two even draws, 0 to 1, weighed `together` and the rest, brought back to an even draw (the sum's own distribution's
 * share under it), so a colony's chance of standing is its place's cover share whatever its garden's weight (M169: the
 * sum itself, weighed 0.8 to 0.2, had stood 16.7% of the reef at 20% cover, 50% killed).
 */
export function evenOf(garden: number, own: number, together: number): number {
  const [a, b] = together >= 0.5 ? [together, 1 - together] : [1 - together, together];
  const t = together * garden + (1 - together) * own;
  if (b <= 0) return t;
  if (t <= b) return (t * t) / (2 * a * b);
  if (t <= a) return (t - b / 2) / a;
  return 1 - ((1 - t) * (1 - t)) / (2 * a * b);
}

/** The highest of `heightAt` over a colony `widthM` across at a point, at its footprint's points (`footprint`, M175). */
export function footTop(x: number, z: number, widthM: number, heightAt: (x: number, z: number) => number): number {
  const at = footprint(x, z, widthM);
  let top = heightAt(x, z);
  for (let k = 2; k < at.length; k += 2) top = Math.max(top, heightAt(at[k] as number, at[k + 1] as number));
  return top;
}

/** The lowest of `heightAt` at a point and eight about it at a colony's half-width: where it is seated (`ReefScene.seatAt`'s rule). */
export function footSeat(x: number, z: number, widthM: number, heightAt: (x: number, z: number) => number): number {
  const r = widthM / 2;
  let low = heightAt(x, z);
  for (let k = 0; k < 8; k++) low = Math.min(low, heightAt(x + r * Math.cos((k * Math.PI) / 4), z + r * Math.sin((k * Math.PI) / 4)));
  return low;
}

/**
 * The colonies' places (M169, the v4 brief's "clusters of varying sizes and densities", "Grouping species according to
 * substrate, depth, and local environmental conditions", "A few large, distinctive colonies should dominate the scene,
 * with smaller juveniles adding detail"): `gardens` of them on the reef's framework where the camera reaches (`x` by
 * `z`), each garden's middle drawn on the framework (its pavement), a framework standing higher taken `high` times as
 * often (the brief's "elevated coral gardens"). Each colony's surface: basalt within `basaltReach` of an outcrop's
 * width, pavement where the framework stands under `pavementM`, else framework; its coral by its place's weights
 * (`coralWeights`, NOAA's surveys), a garden's dominant coral its focal colony's and `own` of the rest (JUDGED: its
 * colonies of one kind together, as fragments of finger and rice coral grow in patches of their own); its length drawn
 * from its coral's measured lengths (the focal colony from `focalFrom` of their spread up), drawn at `size.atMedian` of
 * its form's size at its coral's median length and in proportion to its length to the `power` (JUDGED: NOAA's
 * colonies a median 13 to 21 cm, the kit's forms drawn 0.4 to 1.3 m, M125; since M246 the lengths' own spread, power
 * 1, the v6 brief's "More variation in size": at 0.6 it had been squeezed, "repeated brown colonies sometimes appear at
 * similar sizes"), within `least` and `most`; nearer the middle of its garden, gathered toward it (`gather`, M246).
 * Each stands while its own draw is under its place's living cover's share
 * (`colonyLook`), its garden's draw most of it (`together`), so a garden comes alive together as the cover rises.
 */
function reefColonyPlaces(reef: MauiNuiReef, forms: ReadonlyMap<string, number>, floor: ReturnType<typeof reefFloor>, blend: ReefBlend, outcrops: readonly { x: number; z: number; widthM: number }[], groundAt: (x: number, z: number) => number = (x, z) => floor.heightAt(x, z)): { x: number; z: number; form: string; size: number; yaw: number; shown: number; white: number }[] {
  const s = REEF_SCENE.colonies;
  const seed = reef.seed;
  const out: { x: number; z: number; form: string; size: number; yaw: number; shown: number; white: number }[] = [];
  const most = Math.max(...REEF_SCENE.framework.heightM) * (1 + REEF_SCENE.framework.gardens);
  const formsOf = (coral: KitCoral): string[] => [...forms.keys()].filter((f) => coralOfForm(f) === coral);
  const surfaceAt = (x: number, z: number): Surface =>
    outcrops.some((o) => Math.hypot(o.x - x, o.z - z) < s.basaltReach * o.widthM) ? 'basalt' : floor.reliefAt(x, z) < s.pavementM ? 'pavement' : 'framework';
  const pick = (w: Record<KitCoral, number>, u: number): KitCoral => {
    const kit = Object.keys(w) as KitCoral[];
    let left = u * kit.reduce((m, k) => m + w[k], 0);
    for (const k of kit) if ((left -= w[k]) < 0) return k;
    return kit[kit.length - 1] as KitCoral;
  };
  const median = (coral: KitCoral): number => colonyLengthCm(coral, 0.5);
  // Back from the outcrops' rock, within `clearOf` of an outcrop's width (M169's review: 61 colonies had stood in it;
  // MEASURED, the colonies seated under the rock's top: none kept off half its width; 8 off 0.4 of it, at most 0.46 m
  // under, the user's word after seeing the rock bare at half: "let the corals grow closer").
  const onRock = (x: number, z: number): boolean => outcrops.some((o) => Math.hypot(o.x - x, o.z - z) < s.clearOf * o.widthM);
  // A colony `widthM` across at a point clear of the floor and the rock (M175, the user's word: "the large lobe coral
  // shows in front of the rocky outcropping, which would be physically impossible", "cut off unnaturally by the rocky
  // terrain and looks like it is partially on top of it"): nothing within its footprint, its middle and two rings of eight
  // at a half and the whole of its half-width, rising more than `foot.rise` of its width over its seat (the floor's
  // lowest under it, `ReefScene.seatAt`'s), so the ridge or the rock that stands in front of it stands outside it.
  const footClear = (x: number, z: number, widthM: number): boolean =>
    footTop(x, z, widthM, groundAt) - footSeat(x, z, widthM, (px, pz) => floor.heightAt(px, pz)) <= s.foot.rise * widthM + s.foot.riseM;
  // Each colony's garden and its own draw, its standing draw laid once every garden is (below).
  const of: { garden: number; own: number }[] = [];
  for (let g = 0, t = 0; g < s.gardens && t < s.gardens * 40; t++) {
    const [gx, gz] = [s.x[0] + (s.x[1] - s.x[0]) * hash3(seed, t, 171), s.z[0] + (s.z[1] - s.z[0]) * hash3(seed, t, 172)];
    if (floor.pavementAt(gx, gz) < 0.6 || onRock(gx, gz)) continue;
    // Higher framework kept more often: a draw under its height's share of the most, raised `high` times.
    if (hash3(seed, t, 173) > Math.min(1, (1 + s.high * floor.reliefAt(gx, gz) / most) / (1 + s.high))) continue;
    const n = s.each[0] + Math.floor(hash3(seed, g, 174) * (s.each[1] - s.each[0] + 1));
    const reach = s.radiusM[0] + (s.radiusM[1] - s.radiusM[0]) * hash3(seed, g, 175);
    const strata = blend.strataAt(gx, gz);
    const dominant = pick(coralWeights(strata, surfaceAt(gx, gz)), hash3(seed, g, 184));
    for (let k = 0, tries = 0; k <= n && tries < n * s.foot.tries; tries++) {
      const h = (salt: number): number => hash3(seed * 7 + g, tries, salt);
      const a = 2 * Math.PI * h(176);
      // The focal colony at the garden's middle, or near it where the middle is not clear (M175).
      // The rest about it, gathered toward the middle (M246, `gather`: at a draw's own share of the reach, the colonies
      // the denser the nearer the focal one, sparse at the garden's edge; at its square root, as before, spread evenly).
      const d = k === 0 ? (tries === 0 ? 0 : s.foot.focalM * Math.sqrt(h(177))) : reach * h(177) ** (0.5 + 0.5 * s.gather);
      const [x, z] = [gx + d * Math.cos(a), gz + d * Math.sin(a)];
      if ((k > 0 || d > 0) && (floor.pavementAt(x, z) < 0.5 || onRock(x, z))) continue;
      const coral = k === 0 || h(185) < s.own ? dominant : pick(coralWeights(blend.strataAt(x, z), surfaceAt(x, z)), h(186));
      const choice = formsOf(coral);
      if (choice.length === 0) continue;
      const u = k === 0 ? s.focalFrom + (1 - s.focalFrom) * h(178) : h(178);
      let size = Math.min(s.size.most, Math.max(s.size.least, s.size.atMedian * (colonyLengthCm(coral, u) / median(coral)) ** s.size.power));
      const form = choice[Math.floor(h(179) * choice.length)] as string;
      // Its footprint clear of what rises over its seat (M175): elsewhere in its garden at its own size for the first half
      // of the garden's tries, then smaller, none where even its least is not.
      if (!footClear(x, z, (forms.get(form) ?? 0) * size)) {
        if (tries < (n * s.foot.tries) / 2) continue;
        while (size > s.size.least && !footClear(x, z, (forms.get(form) ?? 0) * size)) size = Math.max(s.size.least, size * s.foot.shrink);
        if (!footClear(x, z, (forms.get(form) ?? 0) * size)) continue;
      }
      out.push({ x, z, form, size, yaw: 2 * Math.PI * h(180), shown: 0, white: h(182) });
      of.push({ garden: g, own: k === 0 ? -1 : h(181) });
      k++;
    }
    g++;
  }
  // The gardens' draws, most of each colony's (`together`), so a garden comes alive together as the cover rises, its
  // focal colony by the garden's draw alone, so as a rule first (the brief's 7: "isolated pockets of life and obvious
  // potential for recovery"); each colony's chance still its place's share of the cover. The gardens in a seeded order,
  // each drawn at its middle's share of the coral the gardens before it lay on the reef (its colonies' footprints, as
  // `fullPercent` counts them), so the gardens standing hold the cover's share of the reef's coral whatever their sizes
  // and wherever they lie (M169: drawn each its own, 22.2% of the reef was drawn coral at 20% cover, and 18.4% in
  // another order, a garden of the few hundred a tenth of it; its focal colony by the garden's alone and none of its own,
  // 22.4%).
  const weight = new Map<number, number>();
  out.forEach((c, i) => {
    const g = (of[i] as { garden: number }).garden;
    const disc = (Math.PI / 4) * ((forms.get(c.form) ?? 0) * c.size) ** 2;
    weight.set(g, (weight.get(g) ?? 0) + (onReef(c.x, c.z) ? disc : 1e-6 * disc));
  });
  const total = [...weight.values()].reduce((a, b) => a + b, 0);
  const draw = new Map<number, number>();
  let before = 0;
  for (const g of [...weight.keys()].sort((a, b) => hash3(seed, a, 183) - hash3(seed, b, 183))) {
    const w = weight.get(g) as number;
    draw.set(g, (before + w / 2) / Math.max(1e-9, total));
    before += w;
  }
  out.forEach((c, i) => {
    const { garden, own } = of[i] as { garden: number; own: number };
    const d = draw.get(garden) as number;
    c.shown = own < 0 ? d : evenOf(d, own, s.together);
  });
  return out;
}

/** The model's pieces laid loose on the Reef's floor, by their ids: its rocks and rubble no wider than `boulders.pieceM`. */
export function reefLoosePieces(model: ReefModel): [string, { part: Part; kind: string }][] {
  return [...model.pieces].filter(([, p]) => (p.kind === 'rock' || p.kind === 'substrate') && spanOf(p.part) <= REEF_SCENE.boulders.pieceM);
}

/**
 * The cells the reef's fish home on, each its island: the reef's cells from `fromFrontM` behind its front to `toBackM`
 * short of its back, all along it (M125, the user's sixth brief: "Since the camera can move in two dimensions,
 * distribute fish activity throughout the reef", "Fish should not follow the camera merely to keep the screen
 * populated"; M118's thinned past the home view's wedge, a school's one cell often alone of its island's, so 11 of the
 * hectare's 12 schools had no way between shelter and feeding ground, MEASURED); each an island's by the islands'
 * weights there (`ReefBlend.islandsAt`), drawn by its own draw.
 */
export function reefFishCells(reef: MauiNuiReef, blend: ReefBlend = sceneBlend(reef)): { cells: number; reefOf: Map<number, number>; at: (column: number) => [number, number] } {
  const s = REEF_SCENE;
  const f = s.fish;
  const cells = Math.round(s.reef.lengthM / f.cellM);
  const half = s.reef.lengthM / 2;
  const front = s.reef.x - s.reef.deepM / 2;
  // The camera's box across the reef.
  const box = [reefCameraBox().min[0], reefCameraBox().max[0]] as const;
  const at = (column: number): [number, number] => [s.reef.x - half + ((column % cells) + 0.5) * f.cellM, -half + (Math.floor(column / cells) + 0.5) * f.cellM];
  const groups = reefGroups(reef, blend);
  // A cell's block, numbered as the cells are, and a block's middle along either way, m from the reef's corner.
  const blockOf = (column: number): number => Math.floor(Math.floor(column / cells) / f.blockCells) * cells + Math.floor((column % cells) / f.blockCells);
  const middle = (k: number): number => (k + 0.5) * f.blockCells * f.cellM;
  const reefOf = new Map<number, number>();
  for (let column = 0; column < cells * cells; column++) {
    const [x, z] = at(column);
    if (x < front + f.fromFrontM || x > front + s.reef.deepM - f.toBackM) continue;
    if (x < box[0] + f.insetM || x > box[1] - f.insetM || Math.abs(z) > s.camera.alongM / 2 - f.insetM) continue;
    // Where the reef stands up: over a group's knoll and its outcrops, and a few on the open reef between.
    const near = groups.some((g) => Math.hypot(x - g.x, z - g.z) <= g.reachM + f.nearM);
    if (!near && hash3(reef.seed, column, 160) >= f.openKeep) continue;
    // Its island drawn for its block of `blockCells` a side, by the weights at the block's middle, so a school's
    // neighbours are its island's and it has a way to go (M125: drawn a cell at a time, 5 of 17 schools that commute
    // had none, MEASURED).
    const block = blockOf(column);
    const weights = blend.islandsAt(s.reef.x - half + middle(block % cells), -half + middle(Math.floor(block / cells)));
    let pick = hash3(reef.seed, block, 161) * weights.reduce((a, b) => a + b, 0);
    let island = weights.length - 1;
    for (let i = 0; i < weights.length; i++) {
      pick -= weights[i] as number;
      if (pick < 0) {
        island = i;
        break;
      }
    }
    reefOf.set(column, island);
  }
  // Every island its blocks, so none of the hectare's fish goes undrawn: an island no block drew takes the block where
  // its weight is the most (Kahoʻolawe's reef 1.9% of the floor, a block of nine cells seldom drawn its own).
  for (let island = 0; island < reef.strata.reduce((n, l) => Math.max(n, l.islandIndex + 1), 0); island++) {
    if ([...reefOf.values()].includes(island)) continue;
    let [best, most] = [-1, -1];
    for (const column of reefOf.keys()) {
      const [x, z] = at(column);
      const w = blend.islandsAt(x, z)[island] ?? 0;
      if (w > most) [best, most] = [blockOf(column), w];
    }
    for (const column of reefOf.keys()) if (blockOf(column) === best) reefOf.set(column, island);
  }
  // None left alone of its island: a school homed there would have no way to go.
  for (const [column, island] of [...reefOf]) {
    const [r, c] = [Math.floor(column / cells), column % cells];
    let alone = true;
    for (let dr = -1; dr <= 1 && alone; dr++) for (let dc = -1; dc <= 1 && alone; dc++) if ((dr || dc) && reefOf.get((r + dr) * cells + c + dc) === island && c + dc >= 0 && c + dc < cells) alone = false;
    if (alone) reefOf.delete(column);
  }
  return { cells, reefOf, at };
}

/**
 * The reef's fish kinds as this scene swims them: a sprite a fish, each commute drawn at most `REEF_SCENE.fish.commuteM`;
 * each species' adults, then its young (M125) in the same order, in schools of their own, drawn by its initial phase's
 * picture at their own length (the user's art draws no young); then the fish counted, not simulated (M231), its adults and
 * its young of `YOUNG_SEEN_MM` or more in one kind, as the divers counted them (`countedIn`).
 */
function localKinds(): FishKind[] {
  const local = (k: FishKind): FishKind => ({ ...k, perSprite: undefined, swim: { ...k.swim, wander: k.swim.commuteM[1] > 0 ? Math.min(1, REEF_SCENE.fish.commuteM / k.swim.commuteM[1]) : 1 } });
  const adults = FISH_KINDS.map(local);
  // Their young low among the coral (M169, the v4 brief's "juvenile fish could shelter within branching coral"; JUDGED).
  return [...adults, ...adults.map((k) => ({ ...k, terminal: undefined, swim: { ...k.swim, cruiseBL: REEF_SCENE.fish.youngCruiseBL } })), ...COUNTED_REEF_KINDS.map(local)];
}

/** A limu's spot as laid (`ReefScene.limuSpots`), before it is packed. */
interface Spot {
  x: number;
  z: number;
  k: number;
  strata: readonly number[];
  y: number;
  depthM: number;
  paved: number;
}

/** The limu's spots packed (M252), each spot's strata's weights `strataEach` in a row of `strata`. */
interface LimuSpots {
  n: number;
  x: Float64Array;
  z: Float64Array;
  k: Uint32Array;
  y: Float64Array;
  depthM: Float64Array;
  paved: Float64Array;
  strataEach: number;
  strata: Float64Array;
}

function packSpots(spots: readonly Spot[]): LimuSpots {
  const each = spots[0]?.strata.length ?? 0;
  const out: LimuSpots = { n: spots.length, x: new Float64Array(spots.length), z: new Float64Array(spots.length), k: new Uint32Array(spots.length), y: new Float64Array(spots.length), depthM: new Float64Array(spots.length), paved: new Float64Array(spots.length), strataEach: each, strata: new Float64Array(spots.length * each) };
  spots.forEach((p, s) => {
    [out.x[s], out.z[s], out.k[s], out.y[s], out.depthM[s], out.paved[s]] = [p.x, p.z, p.k, p.y, p.depthM, p.paved];
    out.strata.set(p.strata, s * each);
  });
  return out;
}

/** Whether a kind of the scene's (`localKinds` order) is a simulated species' young. */
const isYoungKind = (kind: number): boolean => kind >= FISH_KINDS.length && kind < 2 * FISH_KINDS.length;

/** The schools' fish as the hunts and the interactions see them, each told young (a young kind's), initial or terminal. */
const lifeFishOf = (fish: readonly PosableFish[]): LifeFish[] => fish.map((f) => ({ ...f, group: isYoungKind(f.kind) ? 'young' : f.phase === 0 ? 'initial' : 'terminal' }));

/** A point's coral and bottom by its strata's weights and its bands': what is drawn there. */
interface Local {
  cover: number;
  bleached: number;
  killed: number;
}

export class ReefScene implements HabitatScene {
  readonly kind = 'reef' as const;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(50, 1, 0.1, 400);
  readonly profile: HabitatCameraProfile;
  readonly locationName: string;
  readonly blend: ReefBlend;
  readonly floor: ReefFloor & { highest: number };
  /** The floor's shape before it is drawn: its framework's relief at a point (M169). */
  private readonly shape: ReturnType<typeof reefFloor>;
  /** The reef's middle depth by its floor, m: the habitat's water is read at it. */
  readonly middleDepthM: number;
  private readonly paintedFloor: PaintedFloor;
  private readonly water: HabitatWater;
  private readonly key: THREE.DirectionalLight;
  private readonly fill: THREE.HemisphereLight;
  /** The light all round's own strength, before the day's drop (`light.fillDrop`). */
  private readonly fillBase: number;
  private readonly fish: FishSchools;
  /** The predators' strikes now and then where the player watches (M251, `hunts.ts`), sampled from the day's predation. */
  private readonly hunts: ReefHunts;
  /** The animals' interactions with one another and the reef (M265, `reefLife.ts`), and the reef's places they use. */
  private readonly life: ReefLife;
  private lifeAnchors: LifeAnchors | null = null;
  /** The colonies drawn at the day's cover, by place (M265's shelters only in a coral drawn). */
  private readonly coralDrawn = new Set<number>();
  /** The reef's hard bottom and sand as the interactions use them, sampled once its rock is laid (M265). */
  private lifeGround: Anchor[] = [];
  /** Whether the player asks for less motion, as the last frame was told (`animate`): no strike begun then. */
  private calm = false;
  /** The tagged fish and named schools marked (M156). */
  readonly marks = new TagMarks();
  /** The day's honu and monk seal, where one visits (M145). */
  private readonly visitors: HabitatVisitors;
  /** The spinner dolphins resting over the hectare on their days (M216). */
  private readonly dolphins: ReefDolphinPod;
  /** The day octopuses out of their dens (M158). */
  private readonly octopuses: ReefOctopuses;
  private readonly kinds = localKinds();
  private readonly boulders: THREE.Mesh;
  /** The sun's shafts down through the shallows (M169). */
  private readonly shafts: LightShafts;
  /** The water's particles: the suspended, the sediment the parrotfish send up, the bubbles (M173). */
  private readonly particles: ReefParticles;
  private readonly skirt: THREE.Mesh;
  private readonly skirtLit: () => void;
  /** Each floor point's strata's weights (`blend.strataAt`), its bands' and its bottom's turf and macroalgae: fixed, so a day's tint is a sum. */
  private readonly pointWeights: Float32Array;
  private readonly pointBands: Float32Array;
  private readonly pointBottom: Float32Array;
  /** The floor's grid, its points' x and z (M177: where a colony's foot darkens the floor about it). */
  private readonly grid: FloorGrid;
  /** Each floor point's recess, 0 to 1, and its hard bottom's basalt (M177, `REEF_SCENE.recess`, `ReefFloor.basaltAt`). */
  private readonly recess: Float32Array;
  private readonly basalt: Float32Array;
  /** The kit's rocks and rubble in the boulders' places, once the model comes. */
  private pieces: THREE.Mesh | null = null;
  private outcrops: ReefOutcrops | null = null;
  private outcropWeights: { strata: number[]; bands: number[] }[] = [];
  private colonies: { meshes: ColonySprites; places: ReturnType<typeof reefColonyPlaces>; weights: { strata: number[]; bands: number[] }[]; seats: number[] } | null = null;
  /**
   * The spots a limu may stand on (M243, `REEF_SCENE.halimeda`), each its number for its draws, its strata's weights,
   * its floor, its depth and its pavement; laid with the colonies. Packed in arrays of their own (M252, review 29's M249:
   * 39,803 spots as objects, each with an array of its twelve strata's weights, and each limu's weights in arrays of
   * numbers, held 14.97 MB, these 8.12, MEASURED on arrays of their shape with `node --expose-gc`), each value the same.
   */
  private limuSpots: LimuSpots = packSpots([]);
  /** Each limu's weight at each spot, its bottom's and its patch's over their mean (M245): fixed with the spots. */
  private limuWeights: Float64Array[] | null = null;
  /** Each spot's limu's drawn width once seated (M245), by spot and limu; reset with the spots. */
  private limuSeated = new Map<number, number>();
  /** Each piece of gear's seat once laid (M338), by its place in the day's gear; reset with the spots. */
  private gearSeated = new Map<number, { x: number; y: number; z: number; width: number }>();
  /** The gear laid today (M338, `gearLaid`), for the tests. */
  private gearLaidNow: { piece: GearPiece; sprite: Sprite; x: number; y: number; z: number; width: number }[] = [];
  /** The limu laid today, each its plant's (M244), for the tests. */
  private limuLaidNow: { plant: ReefLimu; sprite: Sprite; x: number; y: number; z: number; width: number; lifeWidth: number }[] = [];
  /**
   * The light shadows (M246, `shadows.ts`): the standing things', laid with the coral each day (the colonies, the upright
   * limu and the outcrops' rock), and the swimming animals', laid each frame; the ground they lie on read from a cache
   * of the floor's and the rock's heights, made once the rock is laid.
   */
  private readonly shadowTexture = shadowTexture();
  private readonly standShadows = new ShadowBatch(this.shadowTexture);
  private readonly swimShadows = new ShadowBatch(this.shadowTexture, SHADOWS.creature, DRAPE_STEPS);
  private shadowGround: GroundCache | null = null;
  private readonly swimCast: Shadow[] = [];
  /** The camera's right on the level, the swimming shadows' long way (M263), kept from frame to frame. */
  private readonly shadowAlong: [number, number] = [1, 0];
  /** The swimming shadows' slide from under their casters a meter of height, x and z, by the hour's sun (`slideOf`). */
  private shadowSlide: [number, number] = [0, 0];
  private data: MauiNuiReefData | null = null;
  /** Each group's count as last drawn, by species, group and island (M266, `steadyCount`), and the day it was drawn for. */
  private readonly steady = new Map<string, number>();
  private steadyDay: number | null = null;

  /** Another world laid on the scene (a game loaded, a new one): the counts drawn and the visitors' looks taken afresh from the next day's data (review 31: within `STEADY.resetDays`, the old game's kept). */
  newWorld(): void {
    this.steadyDay = null;
    this.visitors.newWorld();
  }
  /**
   * The cover every colony drawn on the reef makes, standing, percent: its colonies' share of its floor, each by its
   * width across, as a circle (M125); none until the model's are laid, the pavement's paint all the coral until then.
   */
  private fullPercent = 0;
  /** The reef's model, once the page has read it. */
  private model: ReefModel | null = null;
  /** Each of its colonies' forms' width across, m (`widthOf`): found once, not at each colony counted (`disc`). */
  private readonly widths = new Map<string, number>();
  /** The share of the reef's floor that is pavement, where its own coral is painted. */
  private readonly pavedShare: number;
  private tint: [number, number, number] = [1, 1, 1];
  private readonly ahead = new THREE.Vector3();
  private lastViewM = 10;
  /** How far the eye sees through the water: where its fog hides 63% (1 - 1/e) of what lies beyond (`marchComposition`). */
  private readonly seenM = (): number => 1 / Math.max(1e-6, this.water.state().fogPerM);

  /** @param effects the water's particles' settings (M173): the page's address's and its device's unless given */
  constructor(readonly reef: MauiNuiReef, effects: ParticleSettings = pageParticleSettings()) {
    this.locationName = reef.name;
    this.blend = sceneBlend(reef);
    const s = REEF_SCENE;
    // The floor as drawn, its heights the grid's (`drawnFloor`), what stands and swims on it on the floor seen.
    const grid = reefGrid();
    this.grid = grid;
    this.shape = reefFloor(reef, this.blend);
    this.floor = drawnFloor(this.shape, grid);
    this.middleDepthM = this.blend.depthAt(0);
    const half = s.floorM / 2;
    // The floor in the whole tank's paint, rock only on its steepest (the spurs' flanks), sand in the grooves.
    const coral = { live: s.paint.coral.map((hex) => new THREE.Color(hex)), bleached: new THREE.Color(s.paint.bleached), pale: PALE_SHARE };
    this.paintedFloor = new PaintedFloor(this.floor, grid, s.texelM, [ROCK_DEG[0] * 4, ROCK_DEG[1] * 4], true, { coral, patchTexels: s.paint.patchTexels, patchWarp: s.paint.patchWarp, edges: s.paint.edges, causticVary: s.paint.causticVary, shadeCool: s.paint.shadeCool, reef: s.reefPaint, causticTexelM: s.causticTexelM });
    const points = this.paintedFloor.points();
    // Each point's recess (M177, the v5 brief's "Subtle ambient occlusion around crevices", "Subtle contact shadows
    // around stones and rock edges"): how much of the open water above it the floor about it hides, the mean over eight
    // ways of the steepest rise toward any of `recess.radiiM`, `steep` (a rise of 50°) the most.
    const rec = s.recess;
    this.recess = Float32Array.from(points, (p) => {
      const here = this.floor.heightAt(p.x, p.z);
      let hidden = 0;
      for (let k = 0; k < 8; k++) {
        const [cx, cz] = [Math.cos((k * Math.PI) / 4), Math.sin((k * Math.PI) / 4)];
        let rise = 0;
        for (const r of rec.radiiM) rise = Math.max(rise, (this.floor.heightAt(p.x + r * cx, p.z + r * cz) - here) / r);
        hidden += Math.min(1, rise / rec.steep);
      }
      return hidden / 8;
    });
    this.basalt = Float32Array.from(points, (p) => this.shape.basaltAt(p.x, p.z));
    // Its recesses a little darker and cooler in the water's light too (M169's "darker rock recesses", `light.recess`,
    // the paint's tones carrying most of it since M177).
    this.paintedFloor.setShade((i) => 1 - s.light.recess.most * (this.recess[i] as number));
    // The reef's pavement's share of its floor, where its own coral is painted (M125).
    let [paved, count] = [0, 0];
    for (const p of points) {
      if (!onReef(p.x, p.z)) continue;
      paved += this.floor.pavementAt(p.x, p.z);
      count++;
    }
    this.pavedShare = paved / Math.max(1, count);
    const strata = reef.strata.length;
    const bottoms = bottomsOf(reef);
    this.pointWeights = new Float32Array(points.length * strata);
    this.pointBands = new Float32Array(points.length * DEPTH_BINS.length);
    this.pointBottom = new Float32Array(points.length * 3);
    points.forEach((p, i) => {
      const w = this.blend.strataAt(p.x, p.z);
      this.pointWeights.set(w, i * strata);
      this.pointBands.set(this.blend.bandsAt(p.z), i * DEPTH_BINS.length);
      this.pointBottom[i * 3] = w.reduce((m, x, j) => m + x * (bottoms[j] as (typeof bottoms)[number]).turfPercent, 0);
      this.pointBottom[i * 3 + 1] = w.reduce((m, x, j) => m + x * (bottoms[j] as (typeof bottoms)[number]).macroalgaePercent, 0);
      this.pointBottom[i * 3 + 2] = w.reduce((m, x, j) => m + x * (bottoms[j] as (typeof bottoms)[number]).corallinePercent, 0);
    });
    const box = new THREE.Box3(new THREE.Vector3(-half * 4, -(reef.depthRangeM[1] + 40), -half * 4), new THREE.Vector3(half * 4, 0, half * 4));
    // Its own motes not drawn: the particles are (M173).
    this.water = new HabitatWater(box, true, reef.seed, s.light.fogPower, false);
    this.water.fog(this.paintedFloor.skin);
    const { key, fill } = habitatLights();
    // The light from below the sand's, sent back up off it, not the deep's dark (`bounce`); from above `fill` (M169).
    fill.groundColor.setHex(s.bounce);
    fill.color.setHex(s.light.fill);
    this.key = key;
    this.fill = fill;
    this.fillBase = fill.intensity;
    // The floor's skirt out to where the water hides all but a ten-thousandth of it from the camera's box (M125).
    this.skirt = floorSkirt(this.floor, grid, s.skirtM);
    this.skirtLit = skirtLight(this.skirt, this.paintedFloor, grid);
    this.water.fog(fogOnly(this.skirt.material as THREE.MeshStandardMaterial));
    this.boulders = boulders(this.floor, reef.seed, s.boulders.count, s.reef.lengthM + 4, s.boulders.sizeM, [s.reef.x, 0]);
    this.water.fog(fogOnly(this.boulders.material as THREE.MeshStandardMaterial));
    // Its fish: on the reef's cells, each an island's by the islands' weights there.
    const cells = reefFishCells(reef, this.blend);
    const at = (column: number, out: THREE.Vector3): THREE.Vector3 => {
      const [x, z] = cells.at(column);
      return out.set(x, this.groundAt(x, z), z);
    };
    this.fish = new FishSchools(this.kinds, cells.cells, s.fish.cellM / 1000, cells.reefOf, at, (x, z) => this.groundAt(x, z), 0, 1, (s.fish.drawnTimes * 0.001) / UNITS_PER_MM, true, true, () => this.groundTop(), reefCameraBox(), REEF_ROAM);
    this.water.fog(this.fish.fogUniforms());
    // The octopuses' dens: on the cells where the reef stands up, as the fish's homes are, each island's its own (the
    // simulation's octopuses are by island), each a point of its own in its cell (M158).
    const dens = new Map<number, [number, number][]>();
    for (const [column, island] of cells.reefOf) {
      const [x, z] = cells.at(column);
      const list = dens.get(island) ?? [];
      list.push([x + (hash3(reef.seed, column, 1_587) - 0.5) * s.fish.cellM, z + (hash3(reef.seed, column, 1_588) - 0.5) * s.fish.cellM]);
      dens.set(island, list);
    }
    this.octopuses = new ReefOctopuses({ densOf: (island) => dens.get(island) ?? [], groundAt: (x, z) => this.groundAt(x, z), drawnTimes: s.fish.drawnTimes, box: reefCameraBox() });
    this.water.fog(this.octopuses.fogUniforms());
    this.shafts = new LightShafts(reef.seed);
    this.shafts.group.visible = effects.on.shafts;
    this.scene.add(this.shafts.group);
    this.particles = new ReefParticles(reef.seed, effects, REEF_CURRENT);
    for (const fog of this.particles.fogs) this.water.fog(fog);
    this.scene.add(this.particles.group);
    // A parrotfish's bite sends up the sediment at its foot (M173): since M265 told by the interactions (`ReefLife`, below),
    // its school's dives to the reef left off.
    this.scene.add(this.paintedFloor.mesh, this.skirt, this.boulders, this.fish.group, this.octopuses.group, this.water.group, key, key.target, fill);
    this.water.fog(this.standShadows.fogUniforms());
    this.water.fog(this.swimShadows.fogUniforms());
    if (pageShadowsOn()) this.scene.add(this.standShadows.mesh, this.swimShadows.mesh);
    // The camera's home: in front of the reef's middle, looking across it, a little down.
    const h = s.home;
    const yaw = (h.yawDeg * Math.PI) / 180;
    const [tx, tz] = [s.reef.x - s.reef.deepM / 2 + h.aimM, 0];
    const target: [number, number, number] = [tx, Math.min(this.floor.heightAt(tx, tz) + h.aboveFloorM, -h.underM - 0.5), tz];
    const [ex, ez] = [tx - h.backM * Math.cos(yaw), tz + h.backM * Math.sin(yaw)];
    const position: [number, number, number] = [ex, Math.min(Math.max(target[1] + h.upM, this.groundAt(ex, ez) + h.standM), -h.underM), ez];
    // The day's honu and seal across the home view (M145).
    this.visitors = new HabitatVisitors({ middle: new THREE.Vector3(...target), eye: new THREE.Vector3(...position), floorAt: (x, z) => this.groundAt(x, z) });
    // The hunts' fish: the schools', each told as young or not (a young kind's, `isYoungKind`), as a day's rates tell
    // which the simulation's predators take (M252); the octopuses (their names `octo:` and its key), the ulua's prey; and
    // the visitors, for the developer's trigger (a visitor's name is never a fish's).
    this.hunts = new ReefHunts({
      fish: () => [
        ...lifeFishOf(this.fish.posable()),
        ...this.octopuses.posable(), ...this.visitors.posable(),
      ],
      // Set on the layer drawing it; given back on all, as one gone from its layer's drawing is no longer known there.
      pose: (id, pose) => {
        if (!pose) [this.visitors, this.fish, this.octopuses].forEach((layer) => layer.pose(id, null));
        else if (id.startsWith('octo:')) this.octopuses.pose(id, pose);
        else if (this.visitors.posable().some((v) => v.id === id)) this.visitors.pose(id, pose);
        else this.fish.pose(id, pose);
      },
      floorAt: (x, z) => this.groundAt(x, z),
      has: (id) => id.startsWith('octo:') || this.visitors.posable().some((v) => v.id === id) || this.fish.has(id),
    }, huntBudget(typeof location === 'undefined' ? '' : location.search));
    // The animals' interactions (M265, docs/INTERACTIONS_BRIEF.md): the schools' fish as the hunts see them, steered by
    // the fish's own layer; left alone while a hunt holds them; a parrotfish's scrape or a goatfish's probe lifting the
    // sediment as the parrotfish's dives did (M173; `reefLife.ts` tells only those); the predators near told to the
    // octopuses, and the visitors' larger animals among them.
    this.life = new ReefLife({
      fish: () => lifeFishOf(this.fish.posable()),
      steer: (id, steer) => this.fish.steer(id, steer),
      busy: () => this.hunts.busy(),
      // The ground the fish keep over (the floor, the rock and the colonies' tops), so a path kept over it is never set
      // down onto it at once.
      groundAt: (x, z) => this.groundAt(x, z),
      anchors: () => this.lifeAnchors,
      bite: (_kind, x, y, z, key) => {
        this.particles.bite(x, y, z, key, this.camera);
      },
      threats: (near) => this.octopuses.startle([...near, ...this.visitors.posable().filter((v) => v.species === 'seal' || v.species === 'tigerShark' || v.species === 'whitetip').map((v) => ({ species: v.species, at: v.at, width: v.width }))]),
    });
    this.fish.setIndividual(true);
    // Their schools' dives to the reef left off where the interactions draw their feeding, the grazers' and the goatfish's
    // fish by fish (the predators' none, each kind's own `grazes: false`, their feeding the hunts' alone: the brief's "Do
    // not use herbivore-style grazing animations for roi, ulua, or ʻōmilu").
    this.fish.setOwnFeeding(new Set(this.kinds.flatMap((k, i) => (FEEDERS[k.species] ? [i] : []))));
    this.visitors.setReef({
      feedAt: (x, z, withinM, u) => this.lifeAnchors?.near('turf', x, z, withinM, u) ?? this.lifeAnchors?.near('macro', x, z, withinM, u) ?? null,
      shelterAt: (x, z, withinM, u) => this.lifeAnchors?.near('shelter', x, z, withinM, u) ?? null,
    });
    this.water.fog(this.visitors.fogUniforms());
    this.scene.add(this.visitors.group, this.marks.group);
    this.dolphins = new ReefDolphinPod({ middle: new THREE.Vector3(...target), eye: new THREE.Vector3(...position), floorAt: (x, z) => this.groundAt(x, z), box: reefCameraBox() });
    this.water.fog(this.dolphins.fogUniforms());
    this.scene.add(this.dolphins.group);
    const c = s.camera;
    this.profile = {
      home: { position, target },
      fov: habitatFov(s.fov.landscape, s.fov.portrait),
      near: 0.1, far: 400,
      distance: [...c.distance],
      // The camera from level to 25° looking down (M228, the user's word: "lock the vertical viewing angle for the reef to
      // 0 to 25 degrees"; 45° down to 10.8° up before, M117, M122), its home view 24.7° down (MEASURED, M228) within it.
      polar: [(65 * Math.PI) / 180, Math.PI / 2],
      // Lifted to 35° down past a ridge or an outcrop where no tilt within 25° may stand (M241, the overhaul's brief,
      // section 10: "rising to about 35° past ridges smoothly"), eased back once past.
      ridgePolar: (55 * Math.PI) / 180,
      bounds: reefCameraBox(),
      clearance: CLEARANCE_M,
      controls: 'explore',
      turnDeg: c.turnDeg,
    };
  }

  /**
   * The floor's height at a point, under what stands on it: what the camera follows as it is panned (M125); the mean of
   * the floor's within `followM` of it (M169: the framework's ledges and crevices, followed point by point, had moved the
   * camera's point 0.098 m in a frame), so it keeps its height over the seabed, not over each ledge; never more than
   * `followUnderM` under the floor at the point.
   */
  terrainAt(x: number, z: number): number {
    const r = REEF_SCENE.camera.followM;
    let sum = this.floor.heightAt(x, z);
    for (let k = 0; k < 8; k++) {
      const [c, s] = [Math.cos((k * Math.PI) / 4), Math.sin((k * Math.PI) / 4)];
      sum += this.floor.heightAt(x + (r / 2) * c, z + (r / 2) * s) + this.floor.heightAt(x + r * c, z + r * s);
    }
    // Never so far under the floor at the point that the point looked at, `aboveFloorM` over it at home, would meet it
    // (M169's review: the host lifted it over a ridge standing 1.91 m over the mean, and the follow never let it down).
    return Math.max(sum / 17, this.floor.heightAt(x, z) - REEF_SCENE.camera.followUnderM);
  }

  /**
   * The outcrops, the loose colonies and the loose rocks from the reef's model, once the page has read it, with its
   * skins; until then the reef is its floor's pavement and boulders.
   */
  /** @param _textures the kit's skins, the whole tank's; the Reef's rock is painted as its floor is (M177) */
  setReefModel(model: ReefModel, _textures?: ReefTextures): void {
    if (this.outcrops) return;
    const widest = Math.max(...model.outcrops.map((o) => o.widthM));
    const tops = model.outcrops.map((o) => {
      let top = 0;
      for (let i = 1; i < o.rock.position.length; i += 3) top = Math.max(top, o.rock.position[i] as number);
      // Its colonies stand a little above its rock (`tallestM` over all of them).
      return top * 1.25;
    });
    const places = seatedPlaces(model, reefOutcropPlaces(this.reef, model, this.floor, tops, this.blend), widest, (x, z) => this.floor.heightAt(x, z), false);
    this.outcrops = new ReefOutcrops(model, places, widest, ROCK_TILES);
    // The floor the fish keep over read again, the outcrops now standing on it (M159).
    this.fish.floorChanged();
    this.outcropWeights = places.map((p) => this.weightsAt(p.at.x, p.at.z));
    this.outcrops.setLight(...this.tint);
    // Its rock painted as the floor's hard bottom (M177, the v5 brief's "Rounded volcanic and limestone outcrops",
    // "Make the entire environment feel cohesive"): basalt, its knolls' (`ReefFloor.basaltAt`), in the floor's own
    // material, light and fog, its undersides and its foot its recess.
    for (const mesh of this.outcrops.rockMeshes()) this.paintRock(mesh, REEF_SCENE.rocks.outcrops);
    // Its colonies' sprites fogged as the floor is (M164).
    this.water.fog(this.outcrops.colonyFog());
    this.scene.add(this.outcrops.group);
    // The shadows' ground, the floor's and the rock's (M246), its nodes half the floor's fine step apart and on its points
    // (M276: between them, a quarter meter apart, the bilinear rule had cut across the floor's triangles; its first node on
    // one of them, the bounds below whole meters, as the Reef's test holds), its faces marked (M276, `DRAPE_STEP_M`).
    const cb = reefCameraBox();
    // Over the camera's box, the reef's whole floor and the colonies' gardens, whichever reaches farther.
    const [rf, gx, fine] = [REEF_SCENE.reef, REEF_SCENE.colonies.x, REEF_SCENE.fine];
    const step = fine.stepM / 2;
    this.shadowGround?.dispose();
    this.shadowGround = new GroundCache((x, z) => this.rockAt(x, z), Math.min(cb.min[0], rf.x - rf.deepM / 2, gx[0]) - 2, Math.min(cb.min[2], -rf.lengthM / 2) - 2, Math.max(cb.max[0], rf.x + rf.deepM / 2, gx[1]) + 2, Math.max(cb.max[2], rf.lengthM / 2) + 2, step);
    // The swimming's shadows draped on it, over a ledge's corner bent down the rock (M264).
    this.swimShadows.drape(this.shadowGround);
    // The hard bottom and the sand the animals feed on (M265, `layAnchors`), over the camera's box `LIFE_GRID_M` apart:
    // the rock's top or the pavement (`pavementAt` 0.6 or more, the floor's own share of it) its turf; the floor's sand
    // (under 0.3) its sand; each point off the grid's lines by its own draw, so no row of fish lines up on them.
    this.lifeGround = [];
    for (let x = cb.min[0]; x <= cb.max[0]; x += LIFE_GRID_M) {
      for (let z = cb.min[2]; z <= cb.max[2]; z += LIFE_GRID_M) {
        const [px, pz] = [x + LIFE_GRID_M * (hash3(Math.round(x * 4), Math.round(z * 4), 265) - 0.5), z + LIFE_GRID_M * (hash3(Math.round(x * 4), Math.round(z * 4), 266) - 0.5)];
        const [floor, rock] = [this.floor.heightAt(px, pz), this.rockAt(px, pz)];
        const paved = this.floor.pavementAt(px, pz);
        if (rock > floor + 0.2 || paved >= 0.6) this.lifeGround.push({ x: px, y: rock, z: pz, widthM: LIFE_GRID_M, substrate: 'turf', name: rock > floor + 0.2 ? 'rock' : 'pavement' });
        else if (paved < 0.3) this.lifeGround.push({ x: px, y: floor, z: pz, widthM: LIFE_GRID_M, substrate: 'sand', name: 'sand' });
      }
    }
    this.layAnchors();
    // The loose colonies, the user's sprites as the outcrops' are (M164), from their atlas, in the same light and water.
    const colonyPlaces = reefColonyPlaces(this.reef, new Map([...model.forms].map(([form, part]) => [form, spanOf(part)])), { ...this.shape, heightAt: (x, z) => this.floor.heightAt(x, z) }, this.blend, places.map((p) => ({ x: p.at.x, z: p.at.z, widthM: model.outcrops[p.variant]?.widthM ?? 4 })), (x, z) => this.rockAt(x, z));
    const meshes = new ColonySprites(model.forms);
    // The outcrops' colonies drawn among the gardens', far to near together (M172's second review); the outcrops' laid
    // first in `layCoral`.
    meshes.join(this.outcrops.colonies);
    // Every colony, the gardens' and the outcrops', drawn at its footprint's nearest ground from wherever the camera is (M175).
    meshes.setGround((x, z) => this.rockAt(x, z));
    // Their order and stands written for the frame's camera before three sends the attributes, so none shows a frame late.
    this.scene.onBeforeRender = (_renderer, _scene, camera) => meshes.prepare(camera);
    meshes.setLight(...this.tint);
    this.water.fog(meshes.fogUniforms());
    this.scene.add(meshes.group);
    this.colonies = { meshes, places: colonyPlaces, weights: colonyPlaces.map((c) => this.weightsAt(c.x, c.z)), seats: [] };
    // The spots a limu may stand on (M243): `halimeda.spots` over the reef's floor, each its own draws, none on the
    // outcrops' rock (review M243's: there under their colonies, or on a face); each plant its height at the depth its
    // spot stands for.
    const h = REEF_SCENE.halimeda;
    const floorRect = REEF_SCENE.reef;
    const spots: Spot[] = [];
    this.limuWeights = null;
    this.limuSeated.clear();
    this.gearSeated.clear();
    for (let k = 0; k < h.spots; k++) {
      const [x, z] = [floorRect.x - floorRect.deepM / 2 + floorRect.deepM * hash3(this.reef.seed, k, 243), -floorRect.lengthM / 2 + floorRect.lengthM * hash3(this.reef.seed, k, 244)];
      const y = this.floor.heightAt(x, z);
      if (this.rockAt(x, z) > y + h.onRockM) continue;
      spots.push({ x, z, k, strata: this.blend.strataAt(x, z), y, depthM: this.blend.depthAt(z), paved: this.floor.pavementAt(x, z) });
    }
    this.limuSpots = packSpots(spots);
    // The cover every colony on the reef makes, standing (M125): the sim's cover drawn as much as theirs, the rest painted.
    this.model = model;
    for (const [form, part] of model.forms) this.widths.set(form, spanOf(part));
    // Each colony seated at the floor's lowest under half its width (M169's review: on a shelf's lip a fifth of them had
    // stood over the drop, 0.53 m at the tenth worst), sunk a tenth of its size; its picture drawn at its footprint's
    // nearest ground (`ColonySprites.setGround`, M175), its depth at its foot (M172).
    this.colonies.seats = colonyPlaces.map((c) => this.seatAt(c.x, c.z, ((this.widths.get(c.form) ?? 0) * c.size) / 2) - 0.1 * c.size);
    const r = REEF_SCENE.reef;
    let covered = 0;
    for (const p of places) if (onReef(p.at.x, p.at.z)) for (const slot of model.outcrops[p.variant]?.slots ?? []) covered += this.disc(slot.form, slot.matrix.getMaxScaleOnAxis());
    for (const c of colonyPlaces) if (onReef(c.x, c.z)) covered += this.disc(c.form, c.size);
    this.fullPercent = (100 * covered) / (r.lengthM * r.deepM);
    // The kit's rocks and rubble as rubble (M169, the v4 brief's "small rubble ... and other habitat-appropriate
    // detail"; its 1: "Varied sand, volcanic rock, coral rubble"): small, where the framework meets the sand (its
    // pavement's share within `edge`), where broken reef gathers at its foot (JUDGED), each its own draw's piece, turn
    // and size, a little larger where the deep band holds sway; one mesh, in the outcrops' rock.
    const u = REEF_SCENE.rubble;
    const c = REEF_SCENE.colonies;
    const loose = reefLoosePieces(model).map(([, p]) => p);
    if (loose.length > 0 && u.count > 0) {
      const seed = this.reef.seed;
      const laid: { part: Part; matrix: THREE.Matrix4 }[] = [];
      for (let k = 0; laid.length < u.count && k < u.count * 30; k++) {
        const [x, z] = [c.x[0] + (c.x[1] - c.x[0]) * hash3(seed, k, 131), c.z[0] + (c.z[1] - c.z[0]) * hash3(seed, k, 132)];
        const paved = this.floor.pavementAt(x, z);
        if (paved < u.edge[0] || paved > u.edge[1]) continue;
        const piece = loose[Math.floor(hash3(seed, k, 136) * loose.length)] as (typeof loose)[number];
        const scale = (u.scale[0] + (u.scale[1] - u.scale[0]) * hash3(seed, k, 133)) * (1 + u.deeper * (this.blend.bandsAt(z)[2] as number));
        const turn = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), 2 * Math.PI * hash3(seed, k, 137));
        laid.push({ part: piece.part, matrix: new THREE.Matrix4().compose(new THREE.Vector3(x, this.seatAt(x, z, (spanOf(piece.part) * scale) / 2) - 0.1 * scale, z), turn, new THREE.Vector3(scale, scale, scale)) });
      }
      this.pieces = new THREE.Mesh(partGeometry(laidPart(laid)), this.outcrops.materials.rock);
      // Limestone rubble, painted as the floor's hard bottom is (M177).
      this.paintRock(this.pieces, REEF_SCENE.rocks.pieces);
      this.scene.add(this.pieces);
      this.boulders.visible = false;
    }
    if (this.data) this.layCoral(this.data);
    // Its rock lit at once by the light the scene was last given, if any (M177's review: the kit coming after it).
    this.paintedFloor.relight();
  }

  /**
   * A mesh of rock painted as the floor's hard bottom (M177, `PaintedFloor.paintMesh`): of `sub`'s turf, coralline,
   * rubble and basalt, its recess by how far it faces down (`rocks.under` at a face turned straight down) and how near
   * the floor it is (`rocks.foot` within `rocks.footM` of it), its light that much darker as the floor's recesses are.
   */
  private paintRock(mesh: THREE.Mesh, sub: readonly [number, number, number, number]): void {
    const r = REEF_SCENE.rocks;
    this.paintedFloor.paintMesh(mesh, sub, (x, y, z, up) => Math.min(1, Math.max(r.under * Math.max(0, -up), r.foot * Math.max(0, 1 - (y - this.floor.heightAt(x, z)) / r.footM))), (recess) => 1 - REEF_SCENE.light.recess.most * recess);
  }

  /** A colony of a form at a scale, as the reef's cover counts it: a disc its width across, m². */
  private disc(form: string, scale: number): number {
    const width = this.widths.get(form);
    return width === undefined ? 0 : (Math.PI / 4) * (scale * width) ** 2;
  }

  /**
   * The coral drawn on the reef now, percent of its floor (M125, for the tests): its living colonies standing on it
   * (pale and white ones living too), each a disc its width across, and its pavement's own painted coral.
   */
  coralDrawnPercent(): number {
    const r = REEF_SCENE.reef;
    let covered = 0;
    const o = this.outcrops;
    if (o && this.model) {
      for (const c of o.shown) {
        const p = o.places[c.place];
        const slot = p ? this.model.outcrops[p.variant]?.slots[c.slot] : undefined;
        if (c.look !== 'dead' && p && slot && onReef(p.at.x, p.at.z)) covered += this.disc(slot.form, slot.matrix.getMaxScaleOnAxis());
      }
    }
    const at = new THREE.Vector3();
    for (const c of this.colonies?.meshes.drawn() ?? []) {
      at.setFromMatrixPosition(c.matrix);
      if (c.look !== 'dead' && onReef(at.x, at.z)) covered += this.disc(c.form, c.matrix.getMaxScaleOnAxis());
    }
    let [painted, n] = [0, 0];
    this.paintedFloor.points().forEach((p, i) => {
      if (!onReef(p.x, p.z)) return;
      painted += (this.paintedFloor.coralAt(i)[0] as number) * (this.paintedFloor.pavement[i] as number);
      n++;
    });
    return (100 * covered) / (r.lengthM * r.deepM) + (100 * painted) / Math.max(1, n);
  }

  /**
   * The limu standing on the reef today (M243, M244), each of `REEF_LIMU`: each stratum's share of its bottom NOAA's
   * surveys named it (`limuOf`: *Halimeda* 0 to 0.63%, *Padina* 0 to 0.15%), rising and falling with the simulation's
   * macroalgae there against the surveys' (its grazing's, M167; the surveys' share where it holds none); each spot
   * standing a plant where its draw falls under that share of the floor it stands for (the reef's floor over the spots
   * off the outcrops' rock) in plants of its width, so the plants cover the share; each as tall as its limu's `heightM`
   * gives at its depth, as wide as its picture is for that height. Since M245 each limu's patches and bottom weigh each
   * spot, its share kept over the whole reef (within 2% on the suite's day, review M245's) but a stratum's own moved by
   * its patches more than by the simulation's macroalgae there (a stratum's drawn against its asked 0.5 to 2.5 times
   * that day, the macroalgae's 0.92 to 1.21): the patches say where a limu stands, the strata and the simulation how
   * much of it the reef holds.
   */
  private limuLaid(data: MauiNuiReefData): { plant: ReefLimu; sprite: Sprite; x: number; y: number; z: number; width: number; lifeWidth: number }[] {
    const r = REEF_SCENE.reef;
    const seed = this.reef.seed;
    const surveyed = bottomsOf(this.reef);
    const spots = this.limuSpots;
    const spotM2 = (r.deepM * r.lengthM) / Math.max(1, spots.n);
    const out: { plant: ReefLimu; sprite: Sprite; x: number; y: number; z: number; width: number; lifeWidth: number }[] = [];
    const shares = REEF_LIMU.map((limu) => limuOf(this.reef, limu.code).map((p, j) => (data.bottom ? (p * (data.bottom.macroalgaePercent[j] as number)) / Math.max(1e-9, (surveyed[j] as (typeof surveyed)[number]).macroalgaePercent) : p) / 100));
    // Each limu where it grows (M245): the hard bottom alone for those that grow on rock (`hard`), in patches (Pyle et al.
    // 2016, *PeerJ* 4: e2475, CC BY, p. 15: "Macroalgal communities were found in discrete patches (separated by sand or
    // other benthic habitats)"), each its own patches, `limuPatches.cellM` across (JUDGED); each spot's weight its
    // bottom's and its patch's over their mean where it grows, so its share of the floor is kept.
    const lp = REEF_SCENE.limuPatches;
    this.limuWeights ??= REEF_LIMU.map((limu, i) => {
      const w = new Float64Array(spots.n);
      for (let s = 0; s < spots.n; s++) w[s] = (limu.hard ? (spots.paved[s] as number) : 1) * (lp.least + (1 - lp.least) * noise(seed, 245 + i, spots.x[s] as number, spots.z[s] as number, lp.cellM) ** lp.power);
      // Its mean where it grows, each spot by the surveys' share there, so the patches falling off its strata take none of
      // its cover (a plain mean over the reef left Halimeda's 120 plants on the test's day against its 170).
      const surveys = limuOf(this.reef, limu.code);
      const asked = new Float64Array(spots.n);
      for (let s = 0; s < spots.n; s++) {
        let a = 0;
        for (let j = 0; j < surveys.length; j++) a += (spots.strata[s * spots.strataEach + j] as number) * (surveys[j] as number);
        asked[s] = a;
      }
      let [sum, all] = [0, 0];
      for (let s = 0; s < spots.n; s++) sum += (w[s] as number) * (asked[s] as number);
      for (let s = 0; s < spots.n; s++) all += asked[s] as number;
      const mean = sum / Math.max(1e-9, all);
      // A crust's sprites `crust.drawnShare` of its cover (M245): it lies flat on the rock, the rest of it in the floor's
      // macroalgae film, which paints NOAA's macroalgae, the crusts among them (`layCoral`), as the coral's colonies stand
      // for part of its cover and the pavement's paint the rest (M125). Drawn whole, M244's 4,094 small pictures over the floor
      // read as the user's "chicken pox" (JUDGED: a tenth, their beds still seen).
      const drawn = limu.plant === 'peyssonnelia' || limu.plant === 'lobophora' ? REEF_SCENE.crust.drawnShare : 1;
      return w.map((v) => (drawn * v) / Math.max(1e-9, mean));
    });
    const weights = this.limuWeights;
    // One draw a spot, the limu each taking its odds of it in turn, so none stands on another (review M244's: drawn
    // apart, 236 of the day's spots held two to four, a Peyssonnelia over a Padina on one foot).
    for (let s = 0; s < spots.n; s++) {
      const p = { x: spots.x[s] as number, z: spots.z[s] as number, k: spots.k[s] as number, y: spots.y[s] as number, depthM: spots.depthM[s] as number };
      const strata = spots.strata.subarray(s * spots.strataEach, (s + 1) * spots.strataEach);
      let u = hash3(seed, p.k, 245);
      for (let i = 0; i < REEF_LIMU.length; i++) {
        const limu = REEF_LIMU[i] as (typeof REEF_LIMU)[number];
        const share = shares[i] as number[];
        const sprites = LIMU_SPRITES[limu.plant];
        const sprite = sprites[Math.floor(hash3(seed, p.k, limu.salts[1]) * sprites.length)] as Sprite;
        const width = (limu.heightM(p.depthM, sprite) * sprite.width) / sprite.height;
        let here = 0;
        for (let j = 0; j < share.length; j++) here += (strata[j] as number) * (share[j] as number);
        const odds = ((weights[i] as Float64Array)[s] as number) * (here * spotM2) / ((Math.PI / 4) * width * width);
        if (u < odds) {
          // Drawn larger than life to be seen (M245, the user's word: "also make the other seaweeds bigger. they look like
          // tiny specks so the sprite detail is almost pointless"): each but the crusts `fish.drawnTimes` its height, as
          // the fish are drawn, and `limuShown.leastM` tall at least (JUDGED: Microdictyon's 3.5 cm
          // a few pixels from the camera's 2.4 m); still a plant a sprite, its odds by its true size, so the count holds.
          const crust = limu.plant === 'peyssonnelia' || limu.plant === 'lobophora';
          const ls = REEF_SCENE.limuShown;
          const tall = (width * sprite.height) / sprite.width;
          const shown = crust ? width : (Math.max(REEF_SCENE.fish.drawnTimes * tall, ls.leastM) * sprite.width) / sprite.height;
          // Seated clear as a colony is (M175's `colonies.foot`), drawn so large: smaller by `foot.shrink` at a time where
          // the rock or the floor within its footprint rises more than its rule over its seat, to its true size at least
          // (the suite's: a Padina on a face's rock had stood its picture over the slope in front of it; dropped there,
          // not shrunk, a quarter of the Halimeda went).
          // Kept a spot and a limu, its ground never moving (review M245's: worked out each day, 24.5 ms of the day's 43.9).
          const f = REEF_SCENE.colonies.foot;
          const key = s * REEF_LIMU.length + i;
          let seated = this.limuSeated.get(key) ?? shown;
          if (!this.limuSeated.has(key)) {
            while (seated > width && footTop(p.x, p.z, seated, (x, z) => this.rockAt(x, z)) - footSeat(p.x, p.z, seated, (x, z) => this.floor.heightAt(x, z)) > f.rise * seated + f.riseM) seated = Math.max(width, seated * f.shrink);
            this.limuSeated.set(key, seated);
          }
          out.push({ plant: limu.plant, sprite, x: p.x, y: p.y, z: p.z, width: seated, lifeWidth: width });
          break;
        }
        u -= odds;
      }
    }
    return out;
  }

  /**
   * The derelict gear laid on the floor (M338): the k-th piece of the day's (`MauiNuiReefData.gear`, its order fixed, so a
   * cleanup takes the last and the rest stay where they lay) at the first of its own draws (the reef's seed's, not the
   * limu's spots: review M338's, three lines laid on a Microdictyon's foot had taken its shadow) on the hard bottom, off
   * the rock, its picture's footprint seated clear of the rock at its full width as a limu's is (`colonies.foot`), and
   * clear of every piece laid before it; failing `gear.tries` of them (none fails up to `REEF_GEAR_MOST`, MEASURED by the
   * review: 27 tries at most), the first on the hard bottom off the rock. Upright among the limu and the colonies, far to
   * near with them, as a limu is, and among them: a net or a line snags on the coral as on the rock, and the limu about
   * its foot are drawn through it and over it (JUDGED: the review's second counted seven loose colonies and 70 limu
   * within a piece's footprint at the opening; kept clear of the colonies' places, 882 of the 3,383 on the reef's 40 m
   * about its middle, MEASURED, no piece found a seat in its tries, the suite's).
   */
  private gearLaid(gear: readonly GearPiece[]): { piece: GearPiece; sprite: Sprite; x: number; y: number; z: number; width: number }[] {
    const g = REEF_SCENE.gear;
    const r = REEF_SCENE.reef;
    const f = REEF_SCENE.colonies.foot;
    const onRockM = REEF_SCENE.halimeda.onRockM;
    const seats: { x: number; y: number; z: number; width: number }[] = [];
    return gear.map((piece, k) => {
      const sprite = piece.kind === 'line' ? LINE_SPRITE : NET_SPRITES[piece.kind][piece.fouled ? 'fouled' : 'clean'];
      const width = piece.kind === 'large' ? g.largeM : piece.kind === 'small' ? g.smallM : g.lineM;
      let seat = this.gearSeated.get(k);
      if (!seat) {
        let fallback: { x: number; y: number; z: number; width: number } | null = null;
        for (let t = 0; t < g.tries && !seat; t++) {
          const [x, z] = [r.x - r.deepM / 2 + r.deepM * hash3(this.reef.seed, k * g.tries + t, 338), -g.alongM + 2 * g.alongM * hash3(this.reef.seed, k * g.tries + t, 339)];
          const y = this.floor.heightAt(x, z);
          if (this.floor.pavementAt(x, z) < g.hardFrom || this.rockAt(x, z) > y + onRockM) continue;
          fallback ??= { x, y, z, width };
          const seated = footTop(x, z, width, (a, b) => this.rockAt(a, b)) - footSeat(x, z, width, (a, b) => this.floor.heightAt(a, b)) <= f.rise * width + f.riseM;
          if (seated && seats.every((o) => Math.hypot(o.x - x, o.z - z) >= (o.width + width) / 2)) seat = { x, y, z, width };
        }
        seat ??= fallback ?? { x: r.x, y: this.floor.heightAt(r.x, 0), z: 0, width };
        this.gearSeated.set(k, seat);
      }
      seats.push(seat);
      return { piece, sprite, ...seat };
    });
  }

  /** The derelict gear drawn now (M338), for the tests. */
  gearDrawn(): readonly { piece: GearPiece; sprite: Sprite; x: number; y: number; z: number; width: number }[] {
    return this.gearLaidNow;
  }

  /** The limu drawn now (M243), of a plant's alone where one is named (M244), for the tests. */
  limuDrawn(plant?: ReefLimu): readonly { plant: ReefLimu; sprite: Sprite; x: number; y: number; z: number; width: number; lifeWidth: number }[] {
    return this.limuLaidNow.filter((p) => plant === undefined || p.plant === plant);
  }

  /** The *Halimeda* drawn now (M243), for the tests. */
  halimedaDrawn(): readonly { sprite: Sprite; x: number; y: number; z: number; width: number; lifeWidth: number }[] {
    return this.limuDrawn('halimeda');
  }

  /** A point's strata's weights and its bands'. */
  private weightsAt(x: number, z: number): { strata: number[]; bands: number[] } {
    return { strata: this.blend.strataAt(x, z), bands: this.blend.bandsAt(z) };
  }

  /** What is drawn at a point by its weights: its strata's cover, its bands' bleaching and their dead. */
  private localOf(w: { strata: ArrayLike<number>; bands: ArrayLike<number> }, data: MauiNuiReefData, strataAt = 0, bandsAt = 0): Local {
    let cover = 0;
    for (let j = 0; j < data.coverPercent.length; j++) cover += (w.strata[strataAt + j] as number) * (data.coverPercent[j] as number);
    let [bleached, killed] = [0, 0];
    for (let k = 0; k < data.bands.length; k++) {
      const band = data.bands[k] as MauiNuiReefData['bands'][number];
      bleached += (w.bands[bandsAt + k] as number) * band.bleachedPercent;
      killed += (w.bands[bandsAt + k] as number) * band.killedPercent;
    }
    return { cover, bleached, killed };
  }

  /** The top of what is solid under a point: the floor, or an outcrop standing on it. */
  /** The floor's lowest at a point and eight about it `radiusM` out, where a piece of that reach is seated (M169). */
  private seatAt(x: number, z: number, radiusM: number): number {
    return footSeat(x, z, 2 * radiusM, (px, pz) => this.floor.heightAt(px, pz));
  }

  /** The floor or the outcrops' rock, whichever stands higher at a point, their colonies left out (M175). */
  rockAt(x: number, z: number): number {
    return Math.max(this.floor.heightAt(x, z), this.outcrops?.rockTopAt(x, z) ?? -Infinity);
  }

  groundAt(x: number, z: number): number {
    return Math.max(this.floor.heightAt(x, z), this.outcrops?.topAt(x, z) ?? -Infinity);
  }

  /** The highest `groundAt` gives anywhere, or more. */
  private groundTop(): number {
    return Math.max(this.floor.highest, this.outcrops?.highest() ?? -Infinity);
  }

  /** The day's data (`mauiNuiReefData`): the coral on the outcrops, the colonies and the pavement, the fish, the water. */
  setData(data: MauiNuiReefData): void {
    if (data.reef.id !== this.reef.id) throw new Error(`the ${data.reef.id} reef's data laid on the ${this.reef.id} reef`);
    this.data = data;
    this.layCoral(data);
    // The counts drawn kept from day to day while the shares they stand for stay within reach of them (M266,
    // `steadyCount`); started afresh after a jump of the calendar (a game loaded, a long advance).
    if (this.steadyDay === null || Math.abs(data.epochDay - this.steadyDay) >= STEADY.resetDays) this.steady.clear();
    this.steadyDay = data.epochDay;
    const steady = (species: string, group: 'initial' | 'terminal' | 'young', rounded: readonly number[], exact: readonly number[]): number[] => rounded.map((n, island) => {
      const key = `${species}.${group}.${island}`;
      const kept = steadyCount(this.steady.get(key), exact[island] ?? n, n);
      this.steady.set(key, kept);
      return kept;
    });
    const drawn = new Map(data.fish.map((f) => [f.species, { initial: steady(f.species, 'initial', f.initial, f.exact.initial), terminal: steady(f.species, 'terminal', f.terminal, f.exact.terminal), young: steady(f.species, 'young', f.young, f.exact.young) }]));
    const fish: KindCounts[] = this.kinds.map((k, i) => {
      const counted = data.counted.find((x) => x.species === k.species);
      if (i >= 2 * FISH_KINDS.length) {
        const all = (counted?.byIsland ?? []).reduce((a, b) => a + b, 0);
        return { initial: all, terminal: 0, lengthMm: { initial: counted?.lengthMm ?? 0, terminal: 0 }, byIsland: counted?.byIsland ?? [] };
      }
      const f0 = data.fish.find((x) => x.species === k.species);
      const f = f0 ? { ...f0, ...drawn.get(f0.species) } : undefined;
      if (isYoungKind(i)) {
        const young = (f?.young ?? []).reduce((a, b) => a + b, 0);
        return { initial: young, terminal: 0, lengthMm: { initial: f?.lengthMm.young ?? 0, terminal: 0 }, byIsland: f?.young ?? [] };
      }
      const initial = (f?.initial ?? []).reduce((a, b) => a + b, 0);
      const terminal = k.terminal ? (f?.terminal ?? []).reduce((a, b) => a + b, 0) : 0;
      // Each island's schools as many as its fish, its males its own (each phase's split rounded already, `largestRemainder`).
      return { initial, terminal, lengthMm: f?.lengthMm ?? { initial: 0, terminal: 0 }, byIsland: f?.initial ?? [], terminalByIsland: f?.terminal ?? [] };
    });
    this.fish.setCounts(fish);
    this.hunts.setDay(data.epochDay, data.hunts);
    this.visitors.set(data.visitors);
    this.dolphins.set(data.dolphins);
    this.octopuses.set(data.octopuses, data.epochDay, data.lightHours);
    const e = data.environment;
    this.water.setState(e.kdPar.value, e.chlorophyllMgM3.value, e.zooMmolNPerM3.value);
    this.particles.setState(e.kdPar.value, e.chlorophyllMgM3.value, e.zooMmolNPerM3.value);
  }

  /** The coral as the day has it: the outcrops' colonies, the loose ones and the pavement's tint, each place by its strata's cover and its bands' bleaching and dead. */
  private layCoral(data: MauiNuiReefData): void {
    const ceiling = data.ceilingPercent;
    // The colonies standing as the cover is of what they all make (M125; of the reef's ceiling before, at Maui Nui's
    // 13% cover a fifth of them standing, 4% of the floor seen coral, MEASURED), the rest of it painted on the pavement;
    // the living no more than leave room for the skeletons of a heat episode under way beside them (`colonyLook`: the
    // review's, at 13% cover and 30% killed none had stood dead), what they cannot draw painted too.
    const full = Math.max(1e-9, this.fullPercent);
    const living = (l: Local): number => Math.min(l.cover, (1 - l.killed / 100) * this.fullPercent);
    if (this.outcrops) {
      const locals = this.outcropWeights.map((w) => this.localOf(w, data));
      this.outcrops.setCover(locals.map(living), locals.map((l) => l.bleached), locals.map((l) => l.killed), full);
      // The floor the fish keep over read again, the colonies standing on the outcrops changed with the cover (M159's
      // second review: read once, fish hovered over colonies gone and snapped up at those come).
      this.fish.floorChanged();
    }
    if (this.colonies) {
      const m = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const up = new THREE.Vector3(0, 1, 0);
      this.colonies.meshes.clear();
      this.coralDrawn.clear();
      this.colonies.places.forEach((c, i) => {
        const l = this.localOf(this.colonies?.weights[i] as { strata: number[]; bands: number[] }, data);
        const look = colonyLook(c.shown, c.white, living(l) / full, l.bleached, l.killed);
        if (!look) return;
        this.coralDrawn.add(i);
        q.setFromAxisAngle(up, c.yaw);
        this.colonies?.meshes.add(c.form, look, m.compose(new THREE.Vector3(c.x, this.colonies.seats[i] as number, c.z), q, new THREE.Vector3(c.size, c.size, c.size)));
      });
      this.limuLaidNow = this.limuLaid(data);
      this.gearLaidNow = this.gearLaid(data.gear);
      this.colonies.meshes.setPlants([...this.limuLaidNow, ...this.gearLaidNow]);
      this.colonies.meshes.done();
      this.layShadows();
      this.layAnchors();
    }
    // Each colony standing on the floor darkening it about its foot, the gardens' (the outcrops' stand on their rock;
    // M177, the v5 brief's "Better visual attachment at colony bases"): within `contact.reach` of its half-width, to
    // `contact.most` at `contact.full` of it, as a recess is; none where none stands.
    const contact = new Float32Array(this.recess.length);
    const k = REEF_SCENE.contact;
    const { xs, zs } = this.grid;
    const first = (axis: Float64Array, v: number): number => {
      let [lo, hi] = [0, axis.length];
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if ((axis[mid] as number) < v) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    };
    // Not the limu (review M243's: its plants had darkened the floor about them as colonies, on a reef with no coral), told
    // by their feet as the batch holds them, in 32-bit floats.
    const plants = new Set((this.colonies?.meshes.plantsLaid() ?? []).map((p) => `${Math.fround(p.x)},${Math.fround(p.z)}`));
    for (const d of this.colonies?.meshes.draws() ?? []) {
      if (plants.has(`${d.x},${d.z}`)) continue;
      // Not the outcrops' colonies, up on their rock (review M181: two of the suite's day's three stood 0.85 m up it, yet
      // darkened the floor under them; joined to the gardens' batch since M172).
      if (d.y - this.floor.heightAt(d.x, d.z) > k.onFloorM) continue;
      const r = (d.width / 2) * k.reach;
      for (let row = first(zs, d.z - r); row < zs.length && (zs[row] as number) <= d.z + r; row++) {
        for (let col = first(xs, d.x - r); col < xs.length && (xs[col] as number) <= d.x + r; col++) {
          const t = Math.hypot((xs[col] as number) - d.x, (zs[row] as number) - d.z) / (d.width / 2);
          const i = row * xs.length + col;
          contact[i] = Math.max(contact[i] as number, k.most * Math.min(1, Math.max(0, (k.reach - t) / (k.reach - k.full))));
        }
      }
    }
    const s = REEF_SCENE;
    const strata = data.coverPercent.length;
    const bins = DEPTH_BINS.length;
    const meanTurf = bottomsOf(this.reef).reduce((m, b, j) => m + b.turfPercent * (this.reef.areaShare[j] as number), 0);
    const w = { strata: this.pointWeights, bands: this.pointBands };
    // Each point's coral, read once for its own and its tint.
    const locals = Array.from(this.paintedFloor.pavement, (_, i) => this.localOf(w, data, i * strata, i * bins));
    // The pavement's own coral: the cover past what the colonies draw there, over the pavement's share of the floor (since
    // M177 the whole of it: the Reef's paint lays the coral before any growth, where the whole tank's coralline had
    // taken `CORALLINE_SHARE` of it first, the review's 16% short).
    this.paintedFloor.setCoral((i) => {
      const l = locals[i] as Local;
      return [Math.min(1, (l.cover - living(l)) / 100 / Math.max(1e-9, this.pavedShare)), Math.min(1, l.bleached / 100)];
    });
    // The hard bottom's tint, what the skirt beside the floor's edge is drawn in (`PaintedFloor.paintAt`; M177, the shader
    // painting the reef's own tones): its limestone's main tones, or its basalt's by its geology, under its turf's film.
    const rp = s.reefPaint;
    const toneMean = (hexes: readonly number[]): THREE.Color => hexes.slice(0, 3).reduce((m, h) => m.add(new THREE.Color(h)), new THREE.Color(0, 0, 0)).multiplyScalar(1 / 3);
    const [stone, basalt, turf] = [toneMean(rp.limestone), toneMean(rp.basalt), toneMean(rp.turf)];
    // The hard bottom's growths by the simulation and the day (M177, the v5 brief's "Use algae coverage data from the
    // ecological simulation where available", "suitable for degraded reef conditions"): each point's turf, macroalgae and
    // coralline its strata's by their weights, the simulation's bottom where it holds one (its grazing's, M167; its
    // coralline what the sediment, the coral, the turf and the macroalgae leave, as `islandBottom` reads it), NOAA's
    // surveys' otherwise; its turf a film over `cover.turf` of it where its turf is the surveys' mean, more as it is
    // more and as the live coral falls, its macroalgae `macroalgae` times its share added; its coralline `coralline`
    // times its share, drawn on what faces sideways; its rubble the least and more as a heat episode has killed the
    // coral; its basalt its geology's.
    const cv = s.cover;
    const sim = data.bottom;
    const surveyed = bottomsOf(this.reef);
    const subs = new Float32Array(locals.length * 4);
    locals.forEach((l, i) => {
      const live = Math.min(1, l.cover / ceiling);
      let [turfAt, algaeAt, corallineAt] = [this.pointBottom[i * 3] as number, this.pointBottom[i * 3 + 1] as number, this.pointBottom[i * 3 + 2] as number];
      if (sim) {
        [turfAt, algaeAt, corallineAt] = [0, 0, 0];
        for (let j = 0; j < strata; j++) {
          const wj = this.pointWeights[i * strata + j] as number;
          const [t, m] = [sim.turfPercent[j] as number, sim.macroalgaePercent[j] as number];
          turfAt += wj * t;
          algaeAt += wj * m;
          corallineAt += wj * Math.max(0, 100 - ((surveyed[j] as (typeof surveyed)[number]).sedimentPercent) - (data.coverPercent[j] as number) - t - m);
        }
      }
      subs.set([
        Math.min(1, cv.turf * (1 - 0.6 * live) * (turfAt / Math.max(1e-9, meanTurf)) + (cv.macroalgae * algaeAt) / 100),
        Math.min(1, (cv.coralline * corallineAt) / 100),
        Math.min(1, cv.rubble[0] + (cv.rubble[1] * l.killed) / 100),
        this.basalt[i] as number,
      ], i * 4);
    });
    this.paintedFloor.setReef((i) => subs.subarray(i * 4, i * 4 + 4) as unknown as [number, number, number, number], (i) => Math.max(this.recess[i] as number, contact[i] as number));
    this.paintedFloor.tint((_pavement, c, _x, _z, i) => c.copy(stone).lerp(basalt, this.basalt[i] as number).lerp(turf, rp.film * Math.min(1, subs[i * 4] as number)));
    // The skirt in the pavement's new tint at the floor's edge.
    this.skirtLit();
  }

  /** The colonies standing now on the outcrops and the pavement, in each look, for the tests. */
  coralShown(): { outcrops: number; loose: number } & Record<CoralLook, number> {
    const looks = [...(this.outcrops?.shown ?? []).map((s) => s.look), ...(this.colonies?.meshes.drawn() ?? []).map((c) => c.look)];
    return {
      outcrops: this.outcrops?.shown.length ?? 0,
      loose: this.colonies?.meshes.drawn().length ?? 0,
      ...(Object.fromEntries(CORAL_LOOKS.map((look) => [look, looks.filter((l) => l === look).length])) as Record<CoralLook, number>),
    };
  }

  /** The pavement's tint now at a floor point (the first's unless said), for the tests. */
  pavementColor(i = 0): string {
    return this.paintedFloor.colorAt(i);
  }

  /** The floor's points, for the tests. */
  floorPoints(): { x: number; z: number }[] {
    return this.paintedFloor.points();
  }

  setLight(sun: HourSun): void {
    const depth = -this.camera.position.y;
    this.water.setLight(sun, depth);
    this.tint = lightAt(sun, this.key, this.water.seen);
    // The sun's key the stronger by day and the light all round the weaker by as much of its beam (M246, the v6 brief's
    // "Sloping rock faces become progressively darker when facing away from the sunlight"): flat ground about as bright
    // as before, a face turned from the sun darker and one turned to it brighter; by night as before (`light.keyGain`,
    // `light.fillDrop`, JUDGED: a face 15° from the sun's way had moved about 5% of its value, the light all round about
    // half of the ground's, MEASURED by the floor's light at noon).
    const beam = Math.max(0, sun.beam);
    // The shadows by the key as the hour gives it, before its gain: the sun's way and its beam, 0 to 1 (M246); none by
    // the moon (M256, review M253's: taken toward a shade lighter than the night floor, MEASURED 55 in luma at its median
    // against the tints' 75 to 101, they had lightened it).
    const toward = this.key.position.clone().normalize();
    for (const b of [this.standShadows, this.swimShadows]) b.setSun(toward, beam > 0 ? this.key.intensity / KEY_AT_FULL_SUN : 0);
    this.shadowSlide = slideOf(toward);
    this.key.intensity *= 1 + (REEF_SCENE.light.keyGain - 1) * beam;
    this.fill.intensity = this.fillBase - REEF_SCENE.light.fillDrop * beam;
    this.fish.setLight(...this.tint);
    this.visitors.setLight(...this.tint);
    this.dolphins.setLight(...this.tint);
    this.octopuses.setLight(...this.tint);
    this.outcrops?.setLight(...this.tint);
    this.colonies?.meshes.setLight(...this.tint);
    (this.boulders.material as THREE.MeshStandardMaterial).color.setRGB(...this.tint);
    this.paintedFloor.setLight(sun);
    this.paintedFloor.adapt(depth);
    this.shafts.setLight(sun);
    this.particles.setLight(sun, this.shafts.slantNow(), 0);
    this.skirtLit();
  }

  animate(hour: number, dayLengthHours: number, seconds: number, calm: boolean): void {
    this.calm = calm;
    this.fish.swim(hour, dayLengthHours, seconds, calm);
    this.visitors.setHour(hour, dayLengthHours);
    this.visitors.swim(seconds, calm);
    this.dolphins.setHour(hour);
    this.dolphins.swim(seconds, calm);
    this.octopuses.setHour(hour);
    this.octopuses.swim(seconds, calm);
    this.water.follow(this.camera, this.camera.far * 0.8, seconds, calm);
    if (!calm) this.paintedFloor.drift(seconds);
    this.shafts.update(this.camera, seconds, calm);
    this.particles.update({ camera: this.camera, seconds, calm, beams: this.shafts.beams(), viewM: this.viewM() });
  }

  /**
   * How far the camera looks to the floor, m: along its line of sight to where it meets the floor (the floor's own, not
   * the outcrops' tops, which the line slips off at once: M173's review); where it meets none within 40 m, looking up
   * or out, as last found.
   */
  private viewM(): number {
    const ahead = this.camera.getWorldDirection(this.ahead);
    const p = this.camera.position;
    for (let d = 0.5; d < 40; d += 0.5) {
      if (p.y + ahead.y * d <= this.floor.heightAt(p.x + ahead.x * d, p.z + ahead.z * d)) {
        this.lastViewM = d;
        break;
      }
    }
    return this.lastViewM;
  }

  /**
   * The standing things' shadows (M246), each its foot on the ground under it, its width and height: every coral
   * colony drawn (the gardens' and the outcrops'), every upright limu (the crusts none, lying flat: the brief's "Low-profile
   * encrusting algae do not need conspicuous individual cast shadows"), and each outcrop's rock on the floor about it.
   */
  private layShadows(): void {
    const ground = this.shadowGround;
    if (!ground || !this.colonies) return;
    const n = new THREE.Vector3();
    const out: Shadow[] = [];
    // Keyed by their 32-bit places, as the batch keeps them (M243's: its draws' places are its buffers').
    const plants = new Map<string, string>(this.limuLaidNow.map((p) => [`${Math.fround(p.x)},${Math.fround(p.z)}`, p.plant]));
    // The derelict gear standing as an upright limu does (M338), its shadow a plant's.
    for (const p of this.gearLaidNow) plants.set(`${Math.fround(p.x)},${Math.fround(p.z)}`, 'gear');
    for (const d of this.colonies.meshes.draws()) {
      const plant = plants.get(`${Math.fround(d.x)},${Math.fround(d.z)}`);
      if (plant === 'peyssonnelia' || plant === 'lobophora') continue;
      ground.normalAt(d.x, d.z, n);
      out.push(standingShadow(d.x, ground.heightAt(d.x, d.z), d.z, n, d.width, d.height, plant ? SHADOWS.plant : SHADOWS.coral));
    }
    const model = this.model;
    for (const p of this.outcrops?.places ?? []) {
      const widthM = model?.outcrops[p.variant]?.widthM ?? 4;
      const y = this.floor.heightAt(p.at.x, p.at.z);
      out.push(standingShadow(p.at.x, y, p.at.z, n.set(0, 1, 0), widthM, Math.max(0, (this.outcrops?.rockTopAt(p.at.x, p.at.z) ?? y) - y), SHADOWS.rock));
    }
    this.standShadows.set(out);
  }

  /**
   * The reef's places the animals use (M265, `LifeAnchors`): its branching coral (the finger and the cauliflower
   * colonies, Porites compressa's and Pocillopora meandrina's, a young fish's shelter "within branching coral", the v4
   * brief's) and its outcrops' feet (a ledge's or an overhang's) for shelter; its hard bottom (pavement and rock, its turf
   * the grazers') and its sand (the goatfish's), sampled `LIFE_GRID_M` apart once its rock is laid; and its upright limu
   * as laid today (the browsers'), the crusts not.
   */
  private layAnchors(): void {
    const shelters: Anchor[] = [];
    const colonies = this.colonies;
    colonies?.places.forEach((c, i) => {
      // Only a colony drawn (review M267's: most places draw none at the cover, and a fish went into a coral not there).
      if (!this.coralDrawn.has(i)) return;
      const coral = coralOfForm(c.form);
      if (coral !== 'finger' && coral !== 'cauliflower') return;
      shelters.push({ x: c.x, y: colonies.seats[i] ?? this.groundAt(c.x, c.z), z: c.z, widthM: (this.widths.get(c.form) ?? 0.5) * c.size, substrate: 'shelter', name: `${coral} coral` });
    });
    for (const p of this.outcrops?.places ?? []) {
      const widthM = this.model?.outcrops[p.variant]?.widthM ?? 4;
      for (let k = 0; k < 8; k++) {
        const a = (2 * Math.PI * (k + hash3(p.variant, k, 265))) / 8;
        const [x, z] = [p.at.x + 0.5 * widthM * Math.cos(a), p.at.z + 0.5 * widthM * Math.sin(a)];
        shelters.push({ x, y: this.floor.heightAt(x, z), z, widthM: 0.6, substrate: 'shelter', name: 'rock ledge' });
      }
    }
    const plants = this.limuLaidNow.filter((p) => p.plant !== 'peyssonnelia' && p.plant !== 'lobophora').map((p): Anchor => ({ x: p.x, y: p.y, z: p.z, widthM: p.width, substrate: 'macro', name: p.plant }));
    this.lifeAnchors = new LifeAnchors([...shelters, ...plants, ...this.lifeGround]).carry(this.lifeAnchors);
  }

  fit(canvasPx: number, seconds: number, visible: Visible): void {
    // The hunts' poses set before the fish are drawn by them (M251).
    this.hunts.update(this.camera, seconds, canvasPx, this.calm, visible);
    // The interactions' steering, for the fish's next swim (M265).
    this.life.update(this.camera, seconds, this.calm, visible);
    this.fish.fit(this.camera, canvasPx, 0, seconds, visible);
    this.visitors.face(this.camera, visible);
    // The swimming animals' shadows where they are drawn now (M246).
    if (this.shadowGround && this.swimShadows.mesh.parent) {
      // Laid along the sprites as they are drawn, side on to the camera: its right on the level (M263).
      const e = this.camera.matrixWorld.elements;
      this.shadowAlong[0] = e[0] as number;
      this.shadowAlong[1] = e[2] as number;
      this.swimShadows.set(this.swimCast, creatureShadows([this.fish.casters(), this.visitors.casters()], this.shadowGround, this.shadowSlide, this.swimCast, this.shadowAlong));
    }
    this.dolphins.face(this.camera, visible);
    this.octopuses.face(this.camera);
  }

  pick(x: number, y: number, widthPx: number, heightPx: number, withinPx: number): HabitatPick | null {
    // A visitor first: it is drawn over the fish; but a whale, drawn behind those nearer the camera (M210's review: a tap
    // on a fish before it followed the whale), only where no octopus or fish nearer than it is under the tap (M210's
    // second review: one beyond it, hidden by it, is not taken).
    const visitor = this.visitors.pick(this.camera, x, y, widthPx, heightPx, withinPx);
    const away = (p: THREE.Vector3 | undefined): number => (p ? p.distanceTo(this.camera.position) : Infinity);
    // A dolphin (M216), drawn solid as the whale is, before a visitor beyond it, which it hides.
    const dolphin = this.dolphins.pick(this.camera, x, y, widthPx, heightPx, withinPx);
    const visitorAway = visitor ? away(this.visitors.drawnOf(visitor)?.middle) : Infinity;
    // Its distance against a whale's too (M216's review: the whale, solid before it, hides one beyond it).
    if (dolphin && this.dolphins.here() && (!visitor || away(dolphin.middle) < visitorAway)) {
      const [o, f] = [this.octopuses.pick(this.camera, x, y, widthPx, heightPx, withinPx), this.fish.pickNear(this.camera, x, y, widthPx, heightPx, withinPx)];
      const dolphinAway = away(dolphin.middle);
      if (!(o && o.away < dolphinAway) && !(f && f.away < dolphinAway)) return { dolphin: dolphin.id };
    }
    if (visitor && !visitor.startsWith('humpback:')) return { visitor };
    const whaleAway = visitor ? visitorAway : Infinity;
    const nearer = <T extends { away: number }>(t: T | null): T | null => (t && t.away < whaleAway ? t : null);
    // An octopus or a fish, whichever's middle is nearer the tap on the screen (M158's review: the octopus tried first,
    // a fish under the finger beside one could not be taken).
    const octopus = nearer(this.octopuses.pick(this.camera, x, y, widthPx, heightPx, withinPx));
    const fish = nearer(this.fish.pickNear(this.camera, x, y, widthPx, heightPx, withinPx));
    if (octopus && (!fish || octopus.px <= fish.px)) return { octopus: octopus.key };
    return fish ? { school: fish.seed, fish: fish.id } : visitor ? { visitor } : null;
  }

  handOverVisitor(from: string, to: string, tag?: Visitor['tag']): void {
    this.visitors.handOver(from, to, tag);
  }

  /** A dolphin by its name as drawn now, with its group's figures for its card (M216); null where none is, or it is fading out. */
  dolphinAt(id: string): (DolphinDrawn & { inHectare: number; groupMean: number; odds: number }) | null {
    const d = this.dolphins.drawnOf(id);
    const day = this.data?.dolphins;
    return d && day && d.alpha > 0 && this.dolphins.here() ? { ...d, inHectare: day.count, groupMean: day.groupMean, odds: this.data?.dolphinOdds ?? 0 } : null;
  }

  visitorAt(id: string): VisitorAt | null {
    const d = this.visitors.drawnOf(id);
    return d ? visitorAtOf(d, this.data?.visitOdds, this.data?.visits) : null;
  }

  /** An octopus by its key as drawn now, with the hectare's figures for its card (M158); null where it is in its den. */
  octopusAt(key: number): (OctopusDrawn & { inHectare: number; outNow: number; perHa: number }) | null {
    const d = this.octopuses.drawnOf(key);
    return d ? { ...d, inHectare: this.data?.octopuses.length ?? 0, outNow: this.octopuses.outNow(), perHa: this.data?.octopusPerHa ?? 0 } : null;
  }

  visitOf(kind: Visitor['kind']): SpeciesVisitNote | null {
    return this.data?.visits.find((v) => v.species === kind) ?? null;
  }

  visitorGone(id: string): Visitor | null {
    return this.visitors.drawnOf(id) ? null : this.visitors.goneOf(id);
  }

  /** A school as the wildlife card tells it (`FishSchools.schoolAt`), its kind's species, its fish's real depth and the kind's sprites. */
  schoolAt(seed: number): (NonNullable<ReturnType<FishSchools['schoolAt']>> & { species: FishKind['species']; young: boolean; depthM: number; spritesOfKind: number; kindPerSprite: number; level: number }) | null {
    const s = this.fish.schoolAt(seed);
    if (!s) return null;
    const kind = this.kinds[s.kind] as FishKind;
    return { ...s, species: kind.species, young: isYoungKind(s.kind), depthM: -s.middle.y, spritesOfKind: this.fish.spritesOf(s.kind), kindPerSprite: 1, level: this.fish.level() };
  }

  /** The school of a species' adults drawn nearest the home view's point, by its seed; null where none is (M147: a visitor followed home to the reef). */
  nearestSchool(species: FishKind['species']): number | null {
    const kind = FISH_KINDS.findIndex((k) => k.species === species);
    return kind < 0 ? null : this.fish.nearestOf(kind, new THREE.Vector3(...this.profile.home.target));
  }

  /** A species' young of `YOUNG_SEEN_MM` or more a hectare of Maui Nui's reef holds, as the day's data gives it: a young school's card's density. */
  youngPerHa(species: FishKind['species']): number {
    return this.data?.fish.find((f) => f.species === species)?.youngPerHa ?? 0;
  }

  /**
   * A species' adults a hectare of an island's own reef holds, as the day's data gives it: the card's density, the card
   * naming the island its school homes on (the simulation's fish are by island).
   */
  fishPerHa(species: FishKind['species'], island: number): number {
    const counted = this.data?.counted.find((f) => f.species === species);
    if (counted) return counted.perHaByIsland[island] ?? 0;
    return this.data?.islands[island]?.fish.find((f) => f.species === species)?.perHa.value ?? 0;
  }

  /** The school followed, drawn whole whatever the level of detail. */
  holdSchool(seed: number | null): void {
    this.fish.hold(seed);
  }

  /** The one fish followed (M266, Follow Animal), drawn whatever the level of detail and the last of its kind to leave. */
  holdFish(id: string | null): void {
    this.fish.holdFish(id);
  }

  /** A fish drawn, by its name (`FishSchools.fishAt`, M266); null where it is not drawn or is leaving. */
  fishAt(id: string): ReturnType<FishSchools['fishAt']> {
    return this.fish.fishAt(id);
  }

  /** The adults and the young of `YOUNG_SEEN_MM` or more in the habitat's hectare of the kinds the scene draws, all drawn (a species the simulation holds with no kind drawn not among them; the moano kea, held since M179, drawn since M198), the counted (M231) of that length too. */
  fishInHectare(): number {
    const drawn = new Set(this.kinds.map((k) => k.species));
    return this.simulatedInHectare() + (this.data?.counted ?? []).filter((f) => drawn.has(f.species)).reduce((n, f) => n + f.byIsland.reduce((a, b) => a + b, 0), 0);
  }

  /** The simulated species' fish of those (`fishInHectare`), the counted (M231) apart: a count held can never be depleted. */
  private simulatedInHectare(): number {
    const drawn = new Set(this.kinds.map((k) => k.species));
    return (this.data?.fish ?? []).filter((f) => drawn.has(f.species)).reduce((n, f) => n + [...f.initial, ...f.terminal, ...f.young].reduce((a, b) => a + b, 0), 0);
  }

  /**
   * Where the simulated fish are few, a line saying so (M117, the brief: "if simulated populations are genuinely too
   * depleted to support many fish, preserve that ecological truth and make the scarcity understandable to the player"):
   * fewer than `SCARCE` of the simulated species' fish in the hectare, adults and young (the counted, M231, held at their
   * divers' count, not among them); null where there are as many or more.
   */
  scarcity(): string | null {
    const fish = this.simulatedInHectare();
    if (!this.data || fish >= SCARCE) return null;
    return fish === 0
      ? 'No simulated reef fish here: Maui Nui\'s reef holds none of the simulated reef species drawn here.'
      : `Few fish here: an average hectare of Maui Nui's reef holds ${fish} fish of the simulated reef species drawn here, adults and young of ${YOUNG_SEEN_MM / 10} cm or more, a sprite each, each group's drawn count held from day to day while its share stays within reach of it.`;
  }

  /** The fish drawn now, each kind's sprites, for the tests. */
  fishSprites(): number[] {
    return this.kinds.map((_, k) => this.fish.spritesOf(k));
  }

  /** The water's state, for the tests. */
  waterState(): ReturnType<HabitatWater['state']> {
    return this.water.state();
  }

  composition(cols = 30, rows = 50): Composition {
    return marchComposition(this.camera, (x, z) => this.floor.heightAt(x, z), (p) => this.outcrops?.solidAt(p) ?? false, 0, this.seenM(), 0.1, cols, rows);
  }

  /** Where a tag's mark stands (M156): over a school of its kind on its island, its young's for a young one; null for the rest. */
  markAt(target: MarkTarget, remembered: number | null): THREE.Vector3 | null {
    if (!('island' in target)) return visitorMark(target, this.visitors);
    // A tagged octopus over one of its island drawn out of its den (M158's review: none marked), its youth's.
    if (target.species === 'octopus') return this.octopuses.markOf(target.island, target.young === true, remembered, target.tag ?? 0);
    const kind = FISH_KINDS.findIndex((k) => k.species === target.species);
    return kind < 0 ? null : schoolMark(this.fish, kind + (target.young ? FISH_KINDS.length : 0), target.island, remembered);
  }

  /**
   * The developer's trigger (M251, the brief's section 11): a strike begun now by the nearest `predator` in view on a
   * `prey` within its reach (any fish where none is named), caught or missed as asked, counting nothing; whether one
   * could be begun (none where no such pair is in view).
   */
  triggerHunt(canvasPx: number, predator: string, prey: string | null, success: boolean): boolean {
    return this.hunts.trigger(this.camera, canvasPx, predator, prey, success);
  }

  /**
   * The developer's interactions (M265, the brief's section 13), counting nothing: `graze`, `nibble`, `probe`, `scatter`,
   * `shelter` and `chase` the fish's (`ReefLife.trigger`); `split` the school nearest the camera's aim split and
   * rejoining; `honu` a honu here grazing; `whitetip` a whitetip here resting; `octopus` the nearest octopus back to its
   * den. Whether one could be begun.
   */
  triggerLife(name: string): boolean {
    if (name === 'split') return this.fish.splitNear(this.camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8).add(this.camera.position)) !== null;
    if (name === 'honu') return this.visitors.trigger('graze');
    if (name === 'whitetip') return this.visitors.trigger('rest');
    if (name === 'octopus') return this.octopuses.startleNearest(this.camera.position);
    return this.life.trigger(name, this.camera);
  }

  /** The interactions under way, for the developer's overlay (M265): the fish's, and the octopuses' own doings. */
  lifeDebug(): { acts: LifeDebug[]; octopuses: ReturnType<ReefOctopuses['debug']> } {
    return { acts: this.life.debug(), octopuses: this.octopuses.debug() };
  }

  /** The strikes under way, for the developer's overlay (M251). */
  huntsDebug(): HuntDebug[] {
    return this.hunts.debug();
  }

  dispose(): void {
    this.hunts.dispose();
    this.life.reset();
    this.marks.dispose();
    this.standShadows.dispose();
    this.swimShadows.dispose();
    this.shadowGround?.dispose();
    this.shadowTexture.dispose();
    this.fish.dispose();
    this.visitors.dispose();
    this.dolphins.dispose();
    this.octopuses.dispose();
    this.water.dispose();
    this.shafts.dispose();
    this.particles.dispose();
    if (this.outcrops) {
      this.scene.remove(this.outcrops.group);
      this.outcrops.dispose();
    }
    // The loose colonies' and rocks' materials are the outcrops', freed with them; the skins the page's (`reefTextures`).
    this.colonies?.meshes.dispose();
    this.pieces?.geometry.dispose();
    this.paintedFloor.dispose();
    this.skirt.geometry.dispose();
    (this.skirt.material as THREE.Material).dispose();
    this.boulders.geometry.dispose();
    (this.boulders.material as THREE.Material).dispose();
  }
}

/**
 * The box the Reef's camera and the point it looks at keep to, and its fish (`REEF_SCENE.camera`): `acrossM` from
 * `frontM` in front of the reef, `alongM` along it about its middle, from `downM` to 0.6 m under the surface.
 */
function reefCameraBox(): { min: [number, number, number]; max: [number, number, number] } {
  const [s, c] = [REEF_SCENE, REEF_SCENE.camera];
  const front = s.reef.x - s.reef.deepM / 2 - c.frontM;
  return { min: [front, -c.downM, -c.alongM / 2], max: [front + c.acrossM, -0.6, c.alongM / 2] };
}

/** Whether a point is on the reef drawn, `REEF_SCENE.reef`'s rectangle. */
function onReef(x: number, z: number): boolean {
  const r = REEF_SCENE.reef;
  return Math.abs(x - r.x) <= r.deepM / 2 && Math.abs(z) <= r.lengthM / 2;
}

/** The room the Reef's camera keeps over whatever is solid under it, m (JUDGED). */
const CLEARANCE_M = 0.5;
